import React, { useState, useCallback, memo, useEffect, useRef } from 'react';
import './LiveBuild.css';
import { DEVICES } from '../../constants/breakpoints';
import CMSContentElement from '../CMS/CMSContentElement';
import { buildApiUrl } from '../../config/api';
// Temporarily comment out cart imports
// import CartLiveBuild from './CartLiveBuild';
// import cartManager from './CartManager';

// Add contexts at the top of the file
const CurrentElementsContext = React.createContext();
const SiteContext = React.createContext();

// Add template hidden class constant
const templateHiddenClass = 'template-hidden';

// Add custom hooks for both contexts
const useCurrentElements = () => {
  const context = React.useContext(CurrentElementsContext);
  if (!context) {
    throw new Error('useCurrentElements must be used within a CurrentElementsProvider');
  }
  return context;
};

const useSite = () => {
  const context = React.useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};

// Add localStorage key constant
const CART_STORAGE_KEY = 'website-builder-cart';

// Add loadCartFromStorage function at the top level
const loadCartFromStorage = () => {
  try {
    const storedCart = localStorage.getItem(CART_STORAGE_KEY);
    return storedCart ? JSON.parse(storedCart) : [];
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
    return [];
  }
};

// Add this function to calculate cart total
const calculateCartTotal = (cartItems) => {
  let total = 0;
  let itemCount = 0;
  
  cartItems.forEach(item => {
    const price = parseFloat(item.price || '0');
    const quantity = parseInt(item.quantity || '1');
    
    total += price * quantity;
    itemCount += quantity;
  });
  
  return { total: total.toFixed(2), itemCount };
};

const calculateCheckoutTotal = (cartItems) => {
  if (!Array.isArray(cartItems) || cartItems.length === 0) {
    return 0;
  }

  // Calculate subtotal by summing up (price * quantity) for each item
  const subtotal = cartItems.reduce((total, item) => {
    // Convert price to number, handling string prices with $ symbol
    const price = typeof item.price === 'string' ? 
      parseFloat(item.price.replace(/[^0-9.-]+/g, '')) : 
      typeof item.price === 'number' ? 
        item.price : 0;

    // Convert quantity to number, default to 1 if invalid
    const quantity = typeof item.quantity === 'string' ? 
      parseInt(item.quantity, 10) || 1 : 
      typeof item.quantity === 'number' ? 
        item.quantity : 1;

    return total + (price * quantity);
  }, 0);

  return subtotal.toFixed(2);
};

// Add saveCartToStorage function
const saveCartToStorage = (items) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
  }
};

// Update the updateCartUI function
const updateCartUI = (items, updateFn) => {
  if (typeof updateFn === 'function') {
    const { total, itemCount } = calculateCartTotal(items);
    
    updateFn(prev => {
      return prev.map(el => {
        // Update cart total text element
        if (el.attributes?.['data-element'] === 'cart-total-text') {
          return {
            ...el,
            text: `Total: $${total}`,
            attributes: {
              ...el.attributes,
              'data-amount': total
            }
          };
        }
        
        // Update cart button with item count
        if (el.type === 'cart-button') {
          return {
            ...el,
            text: `Cart (${itemCount})`,
            attributes: {
              ...el.attributes,
              'data-count': itemCount.toString()
            }
          };
        }

        // Handle empty-state visibility
        if (el.attributes?.['data-element'] === 'empty-state') {
          return {
            ...el,
            styles: {
              ...el.styles,
              display: items.length === 0 ? 'flex' : 'none'
            }
          };
        }

        // Handle cart container visibility
        if (el.attributes?.['data-element'] === 'cart-container') {
          return {
            ...el,
            styles: {
              ...el.styles,
              display: el.styles.display === 'none' ? 'none' : 'flex'
            }
          };
        }

        // Handle cart wrapper display
        if (el.type === 'cart-wrapper') {
          return {
            ...el,
            styles: {
              ...el.styles,
              display: 'flex'
            }
          };
        }

        return el;
      });
    });
  }
};

// Add saveCartToStorageAndUpdateUI function at the top level
const saveCartToStorageAndUpdateUI = (items, updateFn) => {
  try {
    // Save to localStorage
    saveCartToStorage(items);
    // Update UI
    updateCartUI(items, updateFn);
  } catch (error) {
    console.error('Error in saveCartToStorageAndUpdateUI:', error);
  }
};

// Update getElementStyles to handle elements without classes
const getElementStyles = (element, classStylesRegistry, combinedClassStylesRegistry, selectedDevice) => {
  if (!element) return {};

  // If element has no className, return all styles inline
  if (!element.className) {
    return element.styles || {};
  }

  // If element has classes, only keep essential inline styles
  const essentialStyles = {};
  const { styles } = element;

  // List of properties that should remain inline
  const inlineProperties = [
    // 'position',
    // 'top',
    // 'left',
    // 'right',
    // 'bottom',
    // 'transform',
    // 'zIndex',
    // 'minHeight',
    // 'minWidth',
    // Add other essential properties that need to be inline
  ];

  if (styles) {
    inlineProperties.forEach(prop => {
      if (styles[prop] !== undefined) {
        essentialStyles[prop] = styles[prop];
      }
    });
  }

  return essentialStyles;
};

// Update checkMediaQueries to use correct breakpoint logic
const checkMediaQueries = (previewWidth) => {
  // Fix the breakpoint logic
  const isMobile = previewWidth <= 375;  // Mobile is 375px or less
  const isTablet = previewWidth <= 768 && previewWidth > 375;  // Tablet is between 376px and 768px
  const isDesktop = previewWidth > 768;  // Desktop is greater than 768px
  
  // console.log('Preview Size Status:', {
  //   width: previewWidth,
  //   isMobile,
  //   isTablet,
  //   isDesktop,
  //   breakpoint: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'
  // });
};

// Keep stylesheet reference at module level
let styleSheetRef = null;
let customCssStyleSheetRef = null;

// Update createStyleSheet function
const createStyleSheet = (classStylesRegistry, combinedClassStylesRegistry) => {
  //create a new style sheet
  styleSheetRef = document.createElement('style');
  styleSheetRef.id = 'dynamic-state-styles-live';
  document.head.appendChild(styleSheetRef);

  let cssRules = '';

  // Add animation keyframes at the start
  cssRules += `
    /* Animation Keyframes */
    @keyframes fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes slide-up {
      from {
        transform: translateY(50px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    @keyframes slide-down {
      from {
        transform: translateY(-50px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    @keyframes bounce {
      0%, 100% { 
        transform: scale(1);
        opacity: 1;
      }
      50% { 
        transform: scale(1.1);
        opacity: 1;
      }
    }

    @keyframes rotate {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      25% { transform: translateX(-10px); }
      75% { transform: translateX(10px); }
    }
  `;

  // Function to generate CSS rules for a specific device
  const generateDeviceRules = (device) => {
    let rules = '';
    
    // Add animation trigger rules
    rules += `
      /* Animation trigger rules */
      [data-animation-trigger="onView"] {
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
      }

      [data-animation-trigger="onView"].in-view {
        opacity: 1;
        visibility: visible;
      }

      [data-animation-trigger="onHover"]:hover {
        animation-play-state: running !important;
      }

      [data-animation-trigger="onLoad"] {
        animation-play-state: running !important;
      }

      [data-animation-trigger="onClick"]:active {
        animation-play-state: running !important;
      }
    `;

    // Get all unique class names across all devices
    const allClassNames = new Set();
    Object.values(classStylesRegistry).forEach(registry => {
      registry.forEach((_, className) => allClassNames.add(className));
    });

    // Group classes by their base name
    const classGroups = {};
    allClassNames.forEach(className => {
      if (className.includes('--')) {
        const [baseClass] = className.split('--');
        if (!classGroups[baseClass]) {
          classGroups[baseClass] = { base: null, states: new Set() };
        }
        classGroups[baseClass].states.add(className);
      } else {
        if (!classGroups[className]) {
          classGroups[className] = { base: className, states: new Set() };
        } else {
          classGroups[className].base = className;
        }
      }
    });

    // Get all unique combined class names across all devices
    const allCombinedClassNames = new Set();
    Object.values(combinedClassStylesRegistry).forEach(registry => {
      registry.forEach((_, className) => allCombinedClassNames.add(className));
    });

    // Group combined classes by their base combination
    const combinedClassGroups = {};
    allCombinedClassNames.forEach(className => {
      if (className.includes('--')) {
        const [baseClass] = className.split('--');
        if (!combinedClassGroups[baseClass]) {
          combinedClassGroups[baseClass] = { base: null, states: new Set() };
        }
        combinedClassGroups[baseClass].states.add(className);
      } else {
        if (!combinedClassGroups[className]) {
          combinedClassGroups[className] = { base: className, states: new Set() };
        } else {
          combinedClassGroups[className].base = className;
        }
      }
    });

    // Generate rules for single classes
    Object.entries(classGroups).forEach(([baseClass, { base, states }]) => {
      // Handle base styles
      let baseStyles = {};
      if (device === DEVICES.MOBILE) {
        const tabletStyles = classStylesRegistry[DEVICES.TABLET]?.get(base) || {};
        const mobileStyles = classStylesRegistry[DEVICES.MOBILE]?.get(base) || {};
        baseStyles = { ...tabletStyles, ...mobileStyles };
      } else {
        baseStyles = classStylesRegistry[device]?.get(base) || {};
      }

      // Generate base class rules
      if (base) {
        const cssProperties = Object.entries(baseStyles).map(([prop, value]) => {
          const kebabCase = prop.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
          return `${kebabCase}: ${value};`;
        }).join('\n');

        rules += `
          .${base} {
            ${cssProperties}
          }
        `;
      }

      // Handle state styles for single classes
      states.forEach(stateClass => {
        const [, state] = stateClass.split('--');
        const pseudoClass = state === 'pressed' ? 'active' : 
                           state === 'focused' ? 'focus' : 
                           state;
        
        let stateStyles = {};
        if (device === DEVICES.MOBILE) {
          const tabletStateStyles = classStylesRegistry[DEVICES.TABLET]?.get(stateClass) || {};
          const mobileStateStyles = classStylesRegistry[DEVICES.MOBILE]?.get(stateClass) || {};
          stateStyles = { ...tabletStateStyles, ...mobileStateStyles };
        } else {
          stateStyles = classStylesRegistry[device]?.get(stateClass) || {};
        }

        const cssProperties = Object.entries(stateStyles).map(([prop, value]) => {
          const kebabCase = prop.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
          return `${kebabCase}: ${value};`;
        }).join('\n');

        rules += `
          .${baseClass}:${pseudoClass} {
            ${cssProperties}
            ${Object.keys(stateStyles).length > 0 ? 'transition: all 0.3s ease;' : ''}
          }
        `;
      });
    });

    // Generate rules for combined classes
    Object.entries(combinedClassGroups).forEach(([baseCombination, { base, states }]) => {
      // Handle base styles for combined classes
      let baseCombinedStyles = {};
      if (device === DEVICES.MOBILE) {
        const tabletStyles = combinedClassStylesRegistry[DEVICES.TABLET]?.get(base) || {};
        const mobileStyles = combinedClassStylesRegistry[DEVICES.MOBILE]?.get(base) || {};
        baseCombinedStyles = { ...tabletStyles, ...mobileStyles };
      } else {
        baseCombinedStyles = combinedClassStylesRegistry[device]?.get(base) || {};
      }

      // Generate base combined class rules
      if (base) {
        const combinedSelectors = base.split('+').map(c => `.${c}`).join('');
        const cssProperties = Object.entries(baseCombinedStyles).map(([prop, value]) => {
          const kebabCase = prop.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
          return `${kebabCase}: ${value};`;
        }).join('\n');

        rules += `
          ${combinedSelectors} {
            ${cssProperties}
          }
        `;
      }

      // Handle state styles for combined classes
      states.forEach(stateClass => {
        const [, state] = stateClass.split('--');
        const pseudoClass = state === 'pressed' ? 'active' : 
                           state === 'focused' ? 'focus' : 
                           state;
        
        let stateCombinedStyles = {};
        if (device === DEVICES.MOBILE) {
          const tabletStateStyles = combinedClassStylesRegistry[DEVICES.TABLET]?.get(stateClass) || {};
          const mobileStateStyles = combinedClassStylesRegistry[DEVICES.MOBILE]?.get(stateClass) || {};
          stateCombinedStyles = { ...tabletStateStyles, ...mobileStateStyles };
        } else {
          stateCombinedStyles = combinedClassStylesRegistry[device]?.get(stateClass) || {};
        }

        const combinedSelectors = baseCombination.split('+').map(c => `.${c}`).join('');
        const cssProperties = Object.entries(stateCombinedStyles).map(([prop, value]) => {
          const kebabCase = prop.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
          return `${kebabCase}: ${value};`;
        }).join('\n');

        rules += `
          ${combinedSelectors}:${pseudoClass} {
            ${cssProperties}
            ${Object.keys(stateCombinedStyles).length > 0 ? 'transition: all 0.3s ease;' : ''}
          }
        `;
      });
    });

    return rules;
  };

  // Generate all styles at once with proper cascade and specificity
  cssRules += `
    /* Desktop styles (base) */
    html body .live-preview-container {
      ${generateDeviceRules(DEVICES.DESKTOP)}
    }

    /* Tablet styles */
    html body .live-preview-container[data-device="tablet"] {
      ${generateDeviceRules(DEVICES.TABLET)}
    }

    /* Mobile styles */
    html body .live-preview-container[data-device="mobile"] {
      ${generateDeviceRules(DEVICES.MOBILE)}
    }
  `;

  // Update stylesheet content
  styleSheetRef.textContent = cssRules;
  return styleSheetRef;
};

// Add this function to create custom CSS stylesheet
const createCustomCssStyleSheet = (elements) => {
  if (!customCssStyleSheetRef) {
    customCssStyleSheetRef = document.createElement('style');
    customCssStyleSheetRef.id = 'custom-css-styles-live';
    document.head.appendChild(customCssStyleSheetRef);
  }

  const allRules = [];
  
  elements.forEach(element => {
    if (element['custom-css']) {
      const builderId = element.builderId;

      // Add desktop styles
      if (element['custom-css'][DEVICES.DESKTOP]) {
        allRules.push(`/* Desktop styles for ${builderId} */
html body .live-preview-container {
  [data-builder-id="${builderId}"] {
    ${element['custom-css'][DEVICES.DESKTOP].trim().replace(/\n/g, '\n    ')}
  }
}`);
      }

      // Add tablet styles with desktop fallback
      const tabletStyles = element['custom-css']?.[DEVICES.TABLET] || element['custom-css']?.[DEVICES.DESKTOP] || '';
      if (tabletStyles) {
        allRules.push(`/* Tablet styles for ${builderId} */
html body .live-preview-container[data-device="tablet"] {
  [data-builder-id="${builderId}"] {
    ${tabletStyles.trim().replace(/\n/g, '\n    ')}
  }
}`);
      }

      // Add mobile styles with tablet and desktop fallback
      const mobileStyles = element['custom-css']?.[DEVICES.MOBILE] || 
                         element['custom-css']?.[DEVICES.TABLET] || 
                         element['custom-css']?.[DEVICES.DESKTOP] || '';
      if (mobileStyles) {
        allRules.push(`/* Mobile styles for ${builderId} */
html body .live-preview-container[data-device="mobile"] {
  [data-builder-id="${builderId}"] {
    ${mobileStyles.trim().replace(/\n/g, '\n    ')}
  }
}`);
      }
    }
  });

  // Join all rules with newlines
  const fullCssContent = '/* Custom CSS Styles */\n' + allRules.join('\n\n');
  customCssStyleSheetRef.textContent = fullCssContent;
  return customCssStyleSheetRef;
};

// Helper function to convert string boolean attributes to actual booleans
const convertBooleanAttributes = (attributes) => {
  if (!attributes) return {};
  
  const newAttributes = { ...attributes };
  
  // Convert common boolean attributes from strings to actual booleans
  const booleanAttributes = ['disabled', 'required', 'readonly', 'checked', 'autofocus', 'multiple', 'selected'];
  
  booleanAttributes.forEach(attr => {
    if (newAttributes[attr] === 'true') {
      newAttributes[attr] = true;
    } else if (newAttributes[attr] === 'false') {
      delete newAttributes[attr]; // Remove false boolean attributes entirely
    }
  });
  
  return newAttributes;
};

// Define a click state tracker at the top of the file, before the LiveBuild component
const buttonClickStates = new Map();

// Memoize the Element component to prevent unnecessary re-renders
const Element = memo(({ element, onNavigate, selectedContentItem, ...props }) => {
  // Use destructuring to get selectedDevice from props
  const { selectedDevice } = props;
  const { currentElements, setCurrentElements } = useCurrentElements();
  const { siteId } = useSite();
  const elementRef = useRef(null);

  if (!element) return null;

  // Keep existing attribute processing
  const processedAttributes = convertBooleanAttributes(element.attributes);

  // Update the processAnimationAttributes function in the Element component
  const processAnimationAttributes = () => {
    if (!element.interactions?.events) return {};

    const animationEvents = element.interactions.events.filter(event => event.type === 'animation');
    if (!animationEvents.length) return {};

    const animationAttributes = {
      ref: elementRef,
      'data-has-animations': 'true',
    };

    // Group animations by trigger type
    const animationsByTrigger = {};
    animationEvents.forEach(event => {
      if (!animationsByTrigger[event.trigger]) {
        animationsByTrigger[event.trigger] = [];
      }
      const { animation, duration, delay, iterations } = event.value;
      animationsByTrigger[event.trigger].push(
        `${animation} ${duration} ${delay} ${iterations === 'infinite' ? 'infinite' : iterations}`
      );
    });

    // Apply animations based on trigger type
    Object.entries(animationsByTrigger).forEach(([trigger, animations]) => {
      const combinedAnimations = animations.join(', ');

      switch (trigger) {
        case 'onView':
          animationAttributes['data-animation-onview'] = combinedAnimations;
          break;
        case 'onHover':
          animationAttributes['data-animation-hover'] = combinedAnimations;
          animationAttributes.onMouseEnter = (e) => {
            e.currentTarget.style.animation = combinedAnimations;
          };
          animationAttributes.onMouseLeave = (e) => {
            e.currentTarget.style.animation = 'none';
          };
          break;
        case 'onClick':
          animationAttributes['data-animation-click'] = combinedAnimations;
          const originalClick = animationAttributes.onClick;
          animationAttributes.onClick = (e) => {
            e.currentTarget.style.animation = 'none';
            const reflow = e.currentTarget.offsetHeight;
            e.currentTarget.style.animation = combinedAnimations;
            if (originalClick) originalClick(e);
          };
          break;
        case 'onLoad':
          if (!animationAttributes.style) animationAttributes.style = {};
          animationAttributes.style.animation = combinedAnimations;
          animationAttributes.style.animationFillMode = 'both';
          break;
      }
    });

    return animationAttributes;
  };

  // Add function to process hover events
  const processHoverEvents = () => {
    if (!element.interactions?.events) return {};
    
    const hoverEvents = element.interactions.events.filter(event => event.type === 'hover');
    if (!hoverEvents.length) return {};
    
    const hoverProps = {};
    
    // Extract hover in and hover out events
    const hoverInEvent = hoverEvents.find(event => event.direction === 'in');
    const hoverOutEvent = hoverEvents.find(event => event.direction === 'out');
    
    if (hoverInEvent) {
      // Store original values to restore them on hover out
      const originalStyle = {};
      
      // Add transition effect
      const transitionDuration = hoverInEvent.value.transitionDuration || '0.3s';
      
      hoverProps.style = {
        ...hoverProps.style,
        transition: `all ${transitionDuration} ease`
      };
      
      hoverProps.onMouseEnter = (e) => {
        const el = e.currentTarget;
        
        // Store original values before applying hover effects
        switch (hoverInEvent.action) {
          case 'scale':
            originalStyle.transform = el.style.transform || 'scale(1)';
            break;
          case 'opacity':
            originalStyle.opacity = el.style.opacity || '1';
            break;
          case 'color':
            originalStyle.color = el.style.color;
            break;
        }
        
        // Set the data attribute to store original values
        el.setAttribute('data-original-style', JSON.stringify(originalStyle));
        
        // Apply hover effect
        switch (hoverInEvent.action) {
          case 'scale':
            el.style.transform = `scale(${hoverInEvent.value.scale || 1.1})`;
            break;
          case 'opacity':
            el.style.opacity = hoverInEvent.value.opacity || 0.8;
            break;
          case 'color':
            el.style.color = hoverInEvent.value.color || '#3498db';
            break;
          case 'toggleClass':
            if (hoverInEvent.value.className) {
              // Find target element by ID
              if (hoverInEvent.value.targetId) {
                const targetId = parseInt(hoverInEvent.value.targetId);
                const targetElement = document.querySelector(`[data-element-id="${targetId}"]`);
                
                if (targetElement) {
                  targetElement.classList.add(hoverInEvent.value.className);
                }
              } else {
                // If no targetId, add class to the current element (original behavior)
                el.classList.add(hoverInEvent.value.className);
              }
            }
            break;
          case 'toggle':
            // Handle show/hide element on hover
            if (hoverInEvent.value.targetId) {
              const targetId = parseInt(hoverInEvent.value.targetId);
              const targetElement = document.querySelector(`[data-element-id="${targetId}"]`);
              
              if (targetElement) {
                // Save the original display state to restore it later
                const originalDisplay = targetElement.style.display;
                el.setAttribute('data-target-original-display', originalDisplay);
                
                // Apply the display property
                const displayValue = hoverInEvent.value.display || 'block';
                
                // Apply display with !important
                targetElement.style.setProperty('display', displayValue, 'important');
              }
            }
            break;
        }
      };
    }
    
    if (hoverOutEvent) {
      // Use hoverOutEvent values
      hoverProps.onMouseLeave = (e) => {
        const el = e.currentTarget;
        
        // If there's a specific hover out action, apply it
        if (hoverOutEvent.action) {
          switch (hoverOutEvent.action) {
            case 'scale':
              el.style.transform = `scale(${hoverOutEvent.value.scale || 1})`;
              break;
            case 'opacity':
              el.style.opacity = hoverOutEvent.value.opacity || 1;
              break;
            case 'color':
              el.style.color = hoverOutEvent.value.color || '';
              break;
            case 'toggleClass':
              if (hoverOutEvent.value.className) {
                // Find target element by ID
                if (hoverOutEvent.value.targetId) {
                  const targetId = parseInt(hoverOutEvent.value.targetId);
                  const targetElement = document.querySelector(`[data-element-id="${targetId}"]`);
                  
                  if (targetElement) {
                    targetElement.classList.remove(hoverOutEvent.value.className);
                  }
                } else {
                  // If no targetId, remove class from the current element
                  el.classList.remove(hoverOutEvent.value.className);
                }
              }
              break;
            case 'toggle':
              // Handle show/hide element on hover out with explicit action
              if (hoverOutEvent.value.targetId) {
                const targetId = parseInt(hoverOutEvent.value.targetId);
                const targetElement = document.querySelector(`[data-element-id="${targetId}"]`);
                
                if (targetElement) {
                  const displayValue = hoverOutEvent.value.display || 'none';
                  targetElement.style.setProperty('display', displayValue, 'important');
                }
              }
              break;
          }
        } 
        // Otherwise restore to original state
        else if (hoverInEvent) {
          try {
            const originalStyle = JSON.parse(el.getAttribute('data-original-style') || '{}');
            
            switch (hoverInEvent.action) {
              case 'scale':
                el.style.transform = originalStyle.transform || '';
                break;
              case 'opacity':
                el.style.opacity = originalStyle.opacity || '';
                break;
              case 'color':
                el.style.color = originalStyle.color || '';
                break;
              case 'toggleClass':
                if (hoverInEvent.value.className) {
                  // Find target element by ID
                  if (hoverInEvent.value.targetId) {
                    const targetId = parseInt(hoverInEvent.value.targetId);
                    const targetElement = document.querySelector(`[data-element-id="${targetId}"]`);
                    
                    if (targetElement) {
                      targetElement.classList.remove(hoverInEvent.value.className);
                    }
                  } else {
                    // If no targetId, remove class from the current element
                    el.classList.remove(hoverInEvent.value.className);
                  }
                }
                break;
              case 'toggle':
                // Restore original display of target element
                if (hoverInEvent.value.targetId) {
                  const targetId = parseInt(hoverInEvent.value.targetId);
                  const targetElement = document.querySelector(`[data-element-id="${targetId}"]`);
                  
                  if (targetElement) {
                    // Get the original display from attribute
                    let originalDisplay = el.getAttribute('data-target-original-display') || '';
                    
                    // If original display was empty or 'none', set it back to none
                    if (!originalDisplay || originalDisplay === 'none') {
                      targetElement.style.setProperty('display', 'none', 'important');
                    } else {
                      // Otherwise restore the original display value
                      targetElement.style.setProperty('display', originalDisplay, 'important');
                    }
                  }
                }
                break;
            }
          } catch (error) {
            console.error('Error restoring original style:', error);
          }
        }
      };
    } else if (hoverInEvent) {
      // If no hover out event but there is a hover in event, 
      // add a default hover out handler to reset styles
      hoverProps.onMouseLeave = (e) => {
        const el = e.currentTarget;
        
        try {
          const originalStyle = JSON.parse(el.getAttribute('data-original-style') || '{}');
          
          switch (hoverInEvent.action) {
            case 'scale':
              el.style.transform = originalStyle.transform || '';
              break;
            case 'opacity':
              el.style.opacity = originalStyle.opacity || '';
              break;
            case 'color':
              el.style.color = originalStyle.color || '';
              break;
            case 'toggleClass':
              if (hoverInEvent.value.className) {
                // Find target element by ID
                if (hoverInEvent.value.targetId) {
                  const targetId = parseInt(hoverInEvent.value.targetId);
                  const targetElement = document.querySelector(`[data-element-id="${targetId}"]`);
                  
                  if (targetElement) {
                    targetElement.classList.remove(hoverInEvent.value.className);
                  }
                } else {
                  // If no targetId, remove class from the current element
                  el.classList.remove(hoverInEvent.value.className);
                }
              }
              break;
            case 'toggle':
              // Handle default behavior for toggle on hover out
              if (hoverInEvent.value.targetId) {
                const targetId = parseInt(hoverInEvent.value.targetId);
                const targetElement = document.querySelector(`[data-element-id="${targetId}"]`);
                
                if (targetElement) {
                  // Get the original display from attribute
                  let originalDisplay = el.getAttribute('data-target-original-display') || '';
                  
                  // If original display was empty or 'none', set it back to none
                  if (!originalDisplay || originalDisplay === 'none') {
                    targetElement.style.setProperty('display', 'none', 'important');
                  } else {
                    // Otherwise restore the original display value
                    targetElement.style.setProperty('display', originalDisplay, 'important');
                  }
                }
              }
              break;
          }
        } catch (error) {
          console.error('Error restoring original style:', error);
        }
      };
    }
    
    return hoverProps;
  };

  // Get element classes
  const getElementClasses = () => {
    const classes = ['live-preview-element', element.className];
    return classes.filter(Boolean).join(' ');
  };

  // Get slide width based on parent slider configuration
  const getSlideStyles = () => {
    if (element.sliderRole === 'slide') {
      // Find the parent container element
      const maskElement = currentElements.find(el => el.id === element.parentId);
      if (maskElement) {
        const sliderElement = currentElements.find(el => 
          el.id === maskElement.parentId && 
          el.sliderRole === 'container'
        );
        
        if (sliderElement) {
          // Get device-specific settings or fallback to default
          let slidesPerView = 1;
          
          // First check device-specific settings
          if (sliderElement.deviceSettings) {
            // Determine current device based on window width
            const windowWidth = window.innerWidth;
            
            if (windowWidth <= 768) {
              // Mobile
              slidesPerView = sliderElement.deviceSettings.mobile?.slidesPerView || sliderElement.slidesPerView || 1;
            } else if (windowWidth <= 1024) {
              // Tablet
              slidesPerView = sliderElement.deviceSettings.tablet?.slidesPerView || sliderElement.slidesPerView || 1;
            } else {
              // Desktop
              slidesPerView = sliderElement.deviceSettings.desktop?.slidesPerView || sliderElement.slidesPerView || 1;
            }
          } else {
            // Fallback to legacy slidesPerView property
            slidesPerView = sliderElement.slidesPerView || 1;
          }
          
          return {
            flex: `0 0 ${100/slidesPerView}%`
          };
        }
      }
    }
    return {};
  };

  // Combine all attributes
  const elementProps = {
    ...processedAttributes,
    ...processAnimationAttributes(),
    ...processHoverEvents(), // Add hover event handlers
    id: element.builderId, // Use builderId as the actual DOM element id
    'data-builder-id': element.builderId,
    'data-element-id': element.id, // Add data-element-id attribute for DOM selection
    className: getElementClasses(),
    style: {
      ...getElementStyles(element, props.classStylesRegistry, props.combinedClassStylesRegistry, selectedDevice),
      ...processAnimationAttributes().style, // Merge animation styles
      ...processHoverEvents().style, // Merge hover styles
      ...getSlideStyles(), // Add slide-specific styles
      willChange: element.interactions?.events?.some(e => e.type === 'animation' || e.type === 'hover') ? 'transform, opacity, color' : undefined,
    },
    'data-element-type': element.type,
    // ref: elementRef
  };

  // Handle dynamic content fields
  let displayText = element.text;
  let displaySrc = element.src;

  if (selectedContentItem) {
    if (element.dynamicField) {
      // Handle direct dynamic field mapping
      const dynamicContent = selectedContentItem.data[element.dynamicField];
      if (dynamicContent !== undefined) {
        if (element.type === 'image') {
          displaySrc = dynamicContent;
        } else {
          displayText = dynamicContent;
        }
      }
    } else if (element.text && element.text.includes('{{')) {
      // Handle template variables in text
      displayText = element.text.replace(/\{\{(.*?)\}\}/g, (match, field) => {
        const fieldName = field.trim();
        const value = selectedContentItem.data[fieldName];
        return value !== undefined ? value : match;
      });
    }
  }

  const handleTabInteractionClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Get the clicked element and its element data
    const clickedElement = e.currentTarget;
    const elementId = parseInt(clickedElement.getAttribute('data-element-id'));
    const element = currentElements.find(el => el.id === elementId);
    
    if (!element || !element.interactions) return;
    
    // Find tab interaction event
    const tabEvent = element.interactions.events?.find(event => event.type === 'tab' && event.action === 'tabclick');
    if (!tabEvent) return;
    
    const { tabsParentId, tabButtonClass, tabsContentParentId, tabContentClass } = tabEvent.value;
    
    // Define tabButtons outside the if block so it's accessible throughout the function
    let tabButtons = [];
    let clickedTabIndex = -1;
    
    // 1. Handle tab buttons: Remove the active class from all siblings and add it to the clicked one
    const tabsParent = document.querySelector(`[data-element-id="${tabsParentId}"]`);
    if (tabsParent) {
      // Get ONLY direct children tab buttons (not all descendants)
      tabButtons = Array.from(tabsParent.children).filter(
        child => child.hasAttribute('data-element-id')
      );
            
      // Remove active class from all tab buttons
      tabButtons.forEach(btn => {
        if (btn.classList.contains(tabButtonClass)) {
          btn.classList.remove(tabButtonClass);
        }
      });
      
      // Add active class to the clicked button
      clickedElement.classList.add(tabButtonClass);
      
      // Find the index of the clicked tab within its parent
      clickedTabIndex = tabButtons.findIndex(btn => btn === clickedElement);
    }
    
    // 2. Handle tab content: Hide all content elements except the one associated with this tab
    const contentParent = document.querySelector(`[data-element-id="${tabsContentParentId}"]`);
    if (contentParent) {
      // Get ONLY direct children of the content parent that have data-element-id attribute
      const contentElementsArray = Array.from(contentParent.children).filter(
        child => child.hasAttribute('data-element-id')
      );
            
      // Hide all content elements and remove active class
      contentElementsArray.forEach((content, index) => {
        if (content.classList.contains(tabContentClass)) {
          content.classList.remove(tabContentClass);
        }
      });
      
      // Use index-based matching to show the corresponding content
      if (clickedTabIndex !== -1 && contentElementsArray[clickedTabIndex]) {
        contentElementsArray[clickedTabIndex].classList.add(tabContentClass);
      }
    }
  }

  // Add click handler for interactions
  const handleInteractionClick = (e) => {
    // Add slider arrow click handling
  
    // Handle both button and link elements for arrows 
    if ((element.type === 'button' || element.type === 'link') && element.sliderRole === 'arrow') {
      e.preventDefault(); // Prevent link navigation
      
      const direction = element.direction; // 'left' or 'right'
      const slider = e.currentTarget.closest('[data-slider-role="container"]');
      let mask;
      
      // Find the mask - try different approaches
      if (slider) {
        mask = slider.querySelector('[data-slider-role="mask"]');
      } else {
        // Try to find slider first from parent elements
        const sliderParent = e.currentTarget.closest('[data-element-type="slider"]');
        if (sliderParent) {
          mask = sliderParent.querySelector('[data-slider-role="mask"]');
        }
      }
      
      if (mask && slider) {
        // Find the original slider element to get device-specific settings
        const sliderId = slider.getAttribute('data-element-id');
        const sliderElement = currentElements.find(el => el.id === sliderId);
        
        // Get device-specific configuration or fallback to attributes
        let slidesPerView = parseInt(slider.getAttribute('data-slides-per-view') || '1');
        let slidesPerScroll = parseInt(slider.getAttribute('data-slides-per-scroll') || '1');
        const loopEnabled = slider.getAttribute('data-loop') === 'true';
        
        // If we have the element with device settings, use those based on current screen width
        if (sliderElement && sliderElement.deviceSettings) {
          const windowWidth = window.innerWidth;
          
          if (windowWidth <= 768) {
            // Mobile
            slidesPerView = sliderElement.deviceSettings.mobile?.slidesPerView || slidesPerView;
            slidesPerScroll = sliderElement.deviceSettings.mobile?.slidesPerScroll || slidesPerScroll;
          } else if (windowWidth <= 1024) {
            // Tablet
            slidesPerView = sliderElement.deviceSettings.tablet?.slidesPerView || slidesPerView;
            slidesPerScroll = sliderElement.deviceSettings.tablet?.slidesPerScroll || slidesPerScroll;
          } else {
            // Desktop
            slidesPerView = sliderElement.deviceSettings.desktop?.slidesPerView || slidesPerView;
            slidesPerScroll = sliderElement.deviceSettings.desktop?.slidesPerScroll || slidesPerScroll;
          }
        }
        
        const slides = mask.children.length;
        let currentSlide = parseInt(mask.getAttribute('data-current-slide') || 0);
        
        if (direction === 'left') {
          if (currentSlide === 0 && loopEnabled) {
            // If at beginning and loop is enabled, go to the end
            currentSlide = slides - slidesPerView;
        } else {
            // Normal navigation
            currentSlide = Math.max(0, currentSlide - slidesPerScroll);
          }
        } else {
          if (currentSlide + slidesPerScroll + slidesPerView > slides && loopEnabled) {
            // If would exceed end and loop is enabled, go to the beginning
            currentSlide = 0;
          } else {
            // Normal navigation
            currentSlide = Math.min(slides - slidesPerView, currentSlide + slidesPerScroll);
          }
        }
        
        // Add transition for smooth movement
        mask.style.transition = 'transform 0.5s ease-in-out';
        mask.style.transform = `translateX(-${currentSlide * (100/slidesPerView)}%)`;
        mask.setAttribute('data-current-slide', currentSlide);
      }
    }

    // Handle tab switching if this is a tab button
    if (element.type === 'button' && element.relatedContentId) {
      e.preventDefault();
      
      // Find the current tab button's DOM element
      const clickedTabButton = e.currentTarget;
      
      // Find all related tab buttons in the DOM (siblings)
      const tabButtonsContainer = clickedTabButton.parentElement;
      const allTabButtons = [...tabButtonsContainer.children].filter(
        el => el.getAttribute('data-element-id') && 
        currentElements.find(cel => 
          cel.builderId === el.id && 
          cel.type === 'button'
        )
      );
      
      // Find all tab content elements
      const relatedContentId = element.relatedContentId;
      const allContentElements = document.querySelectorAll(`[data-element-id]`);
      const relevantTabContents = [...allContentElements].filter(el => {
        const contentElement = currentElements.find(cel => cel.builderId === el.id);
        return contentElement && 
               allTabButtons.some(btn => {
                 const btnElement = currentElements.find(cel => cel.builderId === btn.id);
                 return btnElement && btnElement.relatedContentId === contentElement.id;
               });
      });
      
      // Update tab button classes directly in the DOM
      allTabButtons.forEach(btn => {
        if (btn === clickedTabButton) {
          // Make this tab active
          btn.className = btn.className.replace(/\bTab\b/, 'Tab active');
          if (!btn.className.includes('active')) {
            btn.className += ' active';
          }
        } else {
          // Remove active from other tabs
          btn.className = btn.className.replace(/\s*active\b/, '');
        }
      });
      
      // Update tab content visibility directly in the DOM
      relevantTabContents.forEach(content => {
        const contentElement = currentElements.find(cel => cel.builderId === content.id);
        if (contentElement && contentElement.id === relatedContentId) {
          content.style.display = 'block';
        } else {
          content.style.display = 'none';
        }
      });
      
      return;
    }

    // Rest of your existing click handler code...
    if (element.interactions?.events) {
      const clickEvents = element.interactions.events.filter(event => event.type === 'click');
      
      // Find matching click event (first or second click)
      let eventToExecute;
      const firstClickEvent = clickEvents.find(event => event.sequence === 'first');
      const secondClickEvent = clickEvents.find(event => event.sequence === 'second');
      
      // Get the current click count from our tracker or initialize it
      const buttonId = element.id;
      if (!buttonClickStates.has(buttonId)) {
        buttonClickStates.set(buttonId, 0); // Initialize at 0
      }
      const currentClickCount = buttonClickStates.get(buttonId);
      
      if (firstClickEvent && currentClickCount === 0) {
        eventToExecute = firstClickEvent;
        buttonClickStates.set(buttonId, 1); // Update to first click state
      } else if (secondClickEvent && currentClickCount === 1) {
        eventToExecute = secondClickEvent;
        buttonClickStates.set(buttonId, 0); // Reset to initial state
      } else if (firstClickEvent) {
        eventToExecute = firstClickEvent;
        buttonClickStates.set(buttonId, 1); // Ensure first click state is set
      }


      if (eventToExecute) {
        switch (eventToExecute.action) {
          case 'toggle':
            const targetId = parseInt(eventToExecute.value.targetId);
            
            // Use data-element-id attribute for more reliable DOM selection
            const targetDOMElement = document.querySelector(`[data-element-id="${targetId}"]`);
            
            if (targetDOMElement) {
              
              // Apply the display property directly to the DOM with !important
              const displayValue = eventToExecute.value.display || 'block';
              switch (displayValue) {
                case 'block':
                  targetDOMElement.style.setProperty('display', 'block', 'important');
                  break;
                case 'flex':
                  targetDOMElement.style.setProperty('display', 'flex', 'important');
                  break;
                case 'grid':
                  targetDOMElement.style.setProperty('display', 'grid', 'important');
                  break;
                case 'none':
                  targetDOMElement.style.setProperty('display', 'none', 'important');
                  break;
                default:
                  targetDOMElement.style.setProperty('display', displayValue, 'important');
              }
              
              // Note: No React state updates needed as we're manipulating the DOM directly
            } else {
              console.error('Target DOM element not found for id:', targetId);
            }
            break;
          case 'toggleClass':
            const classTargetId = parseInt(eventToExecute.value.targetId);
            
            // Use data-element-id attribute for more reliable DOM selection
            const classTargetElement = document.querySelector(`[data-element-id="${classTargetId}"]`);
            
            if (classTargetElement) {
              const className = eventToExecute.value.className;
              
              if (className) {
                // Check if the class already exists on the element
                if (classTargetElement.classList.contains(className)) {
                  // Remove the class if it exists
                  classTargetElement.classList.remove(className);
                } else {
                  // Add the class if it doesn't exist
                  classTargetElement.classList.add(className);
                }
              }
            } else {
              console.error('Target DOM element not found for toggleClass id:', classTargetId);
            }
            break;
          // ... handle other action types ...
        }
      }
    }

    // Execute navigation if present
    if (element.linkTo || (element.interactions && element.interactions.navigation)) {
      e.preventDefault();
      const pageId = element.linkTo || element.interactions.navigation.pageId;
      if (pageId.startsWith('template-') && selectedContentItem) {
        onNavigate(pageId, selectedContentItem._id);
      } else {
        onNavigate(pageId);
      }
    } 
  };

  // Add a function to extract product data from CMS content
  const extractProductDataFromCMS = (element, contentItem) => {
    if (!contentItem || !contentItem.data) return null;
    
    // Default product data
    const productData = {
      id: `product-${contentItem._id || Date.now()}`,
      name: element.attributes?.['data-product-name'] || displayText || contentItem.data.title || 'Product',
      price: element.attributes?.['data-price'] || contentItem.data.price || '29.99',
      image: element.attributes?.['data-image'] || ''
    };
    
    // Look for cart-specific fields in content item data
    const data = contentItem.data;
    
    // Try to find cart name field (could be named differently)
    Object.keys(data).forEach(key => {
      const fieldValue = data[key];
      const keyLower = key.toLowerCase();
      
      // Look for cart name field
      if (keyLower.includes('name') && (keyLower.includes('cart') || keyLower.includes('product'))) {
        productData.name = fieldValue || productData.name;
      }
      
      // Look for cart price field
      if (keyLower.includes('price') && (keyLower.includes('cart') || keyLower.includes('product'))) {
        productData.price = fieldValue || productData.price;
      }
      
      // Look for cart image field
      if ((keyLower.includes('image') || keyLower.includes('photo') || keyLower.includes('thumbnail')) && 
          (keyLower.includes('cart') || keyLower.includes('product'))) {
        productData.image = fieldValue || productData.image;
      }
    });
    
    // If no specific cart image was found, try to use any image field
    if (!productData.image) {
      Object.keys(data).forEach(key => {
        const fieldValue = data[key];
        const keyLower = key.toLowerCase();
        
        if (keyLower.includes('image') || keyLower.includes('photo') || keyLower.includes('thumbnail')) {
          productData.image = fieldValue || productData.image;
        }
      });
    }
    
    // If still no image, try to find an image element in the parent
    if (!productData.image && element.parentId) {
      const parentElement = currentElements.find(el => el.id === element.parentId);
      if (parentElement) {
        const imageElement = currentElements.find(el => 
          el.parentId === parentElement.id && 
          el.type === 'image'
        );
        if (imageElement && imageElement.src) {
          productData.image = imageElement.src;
        }
      }
    }
    
    return productData;
  };

  // Handle different element types
  switch (element.type) {
    case 'rich-text':
      return (
        <div
          {...elementProps}
          onClick={handleInteractionClick}
          dangerouslySetInnerHTML={{ __html: displayText || '<p>Rich text content</p>' }}
        />
      );

    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6':
      const HeadingTag = element.type;
      return (
        <HeadingTag {...elementProps} onClick={handleInteractionClick}>
          {displayText}
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </HeadingTag>
      );

    case 'checkout-order-item':
      // Only render if this is a template element
      if (element.attributes?.['data-template'] === 'true') {
        return (
          <div {...elementProps} style={{display: 'none'}}>
            {element.children?.map(childId => {
              const childElement = currentElements.find(el => el.id === childId);
              return childElement ? (
                <Element 
                  key={childId}
                  element={childElement}
                  onNavigate={onNavigate}
                  selectedContentItem={selectedContentItem}
                  {...props}
                />
              ) : null;
            })}
          </div>
        );
      } 

      return (
        <div {...elementProps}>
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </div>
      );

    case 'checkout-submit-button':
      // const handleCheckoutSubmit = (e) => {
      //   e.preventDefault();
        
      //   try {
      //     // Check if we're on checkout page
      //     const isCheckoutPage = currentElements.some(el => 
      //       el.attributes?.['data-element'] === 'checkout-section'
      //     );

      //     if (!isCheckoutPage) return;

      //     // Find the form element first
      //     const formElement = currentElements.find(el => el.attributes?.['data-element'] === 'checkout-form');
      //     if (!formElement) {
      //       console.error('Checkout form not found');
      //       return;
      //     }

      //     // Find input elements within the form
      //     const formInputs = currentElements.filter(el => 
      //       el.attributes?.['data-field']?.startsWith('checkout-') && 
      //       el.type === 'input'
      //     );

      //     // Get form data from the input elements
      //     const formData = {
      //       fullName: formInputs.find(el => el.attributes?.['data-field'] === 'checkout-fullname')?.attributes?.value ?? '',
      //       email: formInputs.find(el => el.attributes?.['data-field'] === 'checkout-email')?.attributes?.value ?? '',
      //       address: formInputs.find(el => el.attributes?.['data-field'] === 'checkout-address')?.attributes?.value ?? '',
      //       cardNumber: formInputs.find(el => el.attributes?.['data-field'] === 'checkout-card')?.attributes?.value ?? ''
      //     };

      //     // Get cart data from localStorage
      //     const cartItems = loadCartFromStorage();
          
      //     // Calculate totals
      //     const subtotal = Number(calculateCheckoutTotal(cartItems)) || 0;
      //     const shipping = 9.99; // Default shipping cost
      //     const tax = subtotal * 0.15; // 15% tax rate
      //     const total = subtotal + shipping + tax;

      //     // Compile checkout data
      //     const checkoutData = {
      //       customer: formData,
      //       order: {
      //         items: cartItems,
      //         subtotal: subtotal.toFixed(2),
      //         shipping: shipping.toFixed(2),
      //         tax: tax.toFixed(2),
      //         total: total.toFixed(2)
      //       }
      //     };

      //     // For now, log the checkout data
      //     console.log('Checkout Data:', checkoutData);
          
      //     // TODO: Send to backend
      //     // Clear cart after successful checkout
      //     // saveCartToStorage([]);
      //     // updateCartUI([], updateFn);
      //   } catch (error) {
      //     console.error('Error in handleCheckoutSubmit:', error);
      //   }
      // }; 

      return (
        <button {...elementProps} type="submit">
          {element.text || 'Complete Purchase'}
        </button>
      );

    // Add special handling for cart elements
    case 'cart':
      return (
        <div
          {...elementProps}
          onClick={handleInteractionClick}
        >
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </div>
      );

    case 'cart-wrapper':
      return (
        <div
          {...elementProps}
          onClick={handleInteractionClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby={element.attributes?.['aria-labelledby']}
          // hidden={element.styles?.display === 'none'}
          data-element="cart-wrapper"
        >
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </div>
      );

    case 'cart-button':
      return (
        <button
          {...elementProps}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // Find cart container in currentElements
            const cartContainer = currentElements.find(el => 
              el.parentId === element.parentId && 
              el.attributes?.['data-element'] === 'cart-container'
            );
            
            if (cartContainer) {
              const isVisible = cartContainer.styles?.display !== 'none';
              
              setCurrentElements(prev => 
                prev.map(el => {
                  // Update cart container display
                  if (el.id === cartContainer.id) {
                    return { 
                      ...el, 
                      styles: { 
                        ...el.styles, 
                        display: isVisible ? 'none' : 'flex' 
                      }
                    };
                  }
                  // Update cart wrapper display
                  if (el.parentId === cartContainer.id && el.type === 'cart-wrapper') {
                    return {
                      ...el,
                      styles: {
                        ...el.styles,
                        display: isVisible ? 'none' : 'flex'
                      }
                    };
                  }
                  return el;
                })
              );

              // Update button attributes
              e.currentTarget.setAttribute('aria-expanded', (!isVisible).toString());
            }
          }}
          aria-haspopup="dialog"
          aria-expanded="false"
        >
          {element.text}
        </button>
      );

    case 'cart-content':
      return (
        <div
          {...elementProps}
          onClick={handleInteractionClick}
          role="list"
        >
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </div>
      );

    
    case 'add-to-cart':
      const productData = selectedContentItem ? 
        extractProductDataFromCMS(element, selectedContentItem) : {
          name: element.attributes?.['data-product-name'] || displayText || 'Product',
          price: element.attributes?.['data-price'] || '00.00',
          image: element.attributes?.['data-image'] || ''
        };

      // Merge element attributes with product data
      const mergedAttributes = {
        ...element.attributes,
        'data-product-id': productData.id,
        'data-product-name': productData.name,
        'data-price': productData.price,
        'data-image': productData.image,
        'data-element': 'add-to-cart',
        'data-action': 'add-to-cart',
        'aria-label': 'Add item to cart',
        'type': 'button'
      };

      return (
        <button
          {...elementProps}
          {...mergedAttributes}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            // Get product data from the clicked button's data attributes
            const button = e.currentTarget;
            const clickedProductData = {
              id: button.getAttribute('data-product-id'),
              name: button.getAttribute('data-product-name'),
              price: parseFloat(button.getAttribute('data-price')),
              image: button.getAttribute('data-image'),
              quantity: 1
            };

            console.log('Clicked product data:', clickedProductData);

            // Find cart for current page
            const cart = currentElements.find(el => 
              el.type === 'cart' && 
              el.cartPageId === props.currentPageId
            );
            if (!cart) return;

            // Find cart content in currentElements
            const cartContent = currentElements.find(el => 
              el.type === 'cart-content'
            );
            if (!cartContent) return;

            // Find test product in currentElements
            const testProduct = currentElements.find(el => 
              el.parentId === cartContent.id && 
              el.attributes && 
              el.attributes['data-element'] === 'cart-item' &&
              el.attributes['data-product-id'] === 'Test-Product-1'
            );
            if (!testProduct) return;

            // Load current cart items from storage
            let cartItems = [];
            try {
              const storedCart = localStorage.getItem(CART_STORAGE_KEY);
              cartItems = storedCart ? JSON.parse(storedCart) : [];
            } catch (error) {
              console.error('Error loading cart from storage:', error);
            }

            // Check if product already exists in cart
            const existingItemIndex = cartItems.findIndex(item => item.id === clickedProductData.id);

            if (existingItemIndex !== -1) {
              // Update quantity of existing item
              cartItems[existingItemIndex].quantity += 1;
              saveCartToStorageAndUpdateUI(cartItems, setCurrentElements);

              // Update UI
              setCurrentElements(prev => 
                prev.map(el => {
                  if (el.attributes?.['data-product-id'] === clickedProductData.id && 
                      el.attributes?.['data-element'] === 'cart-item') {
                    return {
                      ...el,
                      attributes: {
                        ...el.attributes,
                        'data-quantity': (cartItems[existingItemIndex].quantity).toString()
                      }
                    };
                  }
                  if (el.attributes?.['data-element'] === 'quantity-value' && 
                      el.parentId === existingItemIndex) {
                    return {
                      ...el,
                      text: cartItems[existingItemIndex].quantity.toString()
                    };
                  }
                  return el;
                })
              );
            } else {
              // Add new item to cart storage
              cartItems.push(clickedProductData);
              saveCartToStorageAndUpdateUI(cartItems, setCurrentElements);

              // Clone test product structure and update UI
              const newElements = [];
              const processElement = (baseElement, parentId) => {
                const newElement = {
                  ...baseElement,
                  id: Date.now() + Math.floor(Math.random() * 1000),
                  builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                  parentId,
                  children: [], // Initialize empty children array
                  styles: { ...baseElement.styles, display: 'flex' }
                };

                // Update attributes based on element's data-element attribute
                const elementRole = baseElement.attributes?.['data-element'];
                
                switch (elementRole) {
                  case 'cart-item':
                    newElement.attributes = {
                      ...newElement.attributes,
                      'data-product-id': clickedProductData.id,
                      'data-price': clickedProductData.price,
                      'data-quantity': clickedProductData.quantity.toString()
                    };
                    break;
                  case 'product-name':
                    newElement.text = clickedProductData.name;
                    break;
                  case 'product-price':
                    newElement.text = `$${clickedProductData.price}`;
                    newElement.attributes = {
                      ...newElement.attributes,
                      'data-price': clickedProductData.price
                    };
                    break;
                  case 'product-image':
                    newElement.src = clickedProductData.image;
                    break;
                  case 'quantity-value':
                    newElement.text = clickedProductData.quantity.toString();
                    newElement.attributes = {
                      ...newElement.attributes,
                      'data-element': 'quantity-value',
                      'data-quantity': clickedProductData.quantity.toString(),
                      'data-product-id': clickedProductData.id  // Add this line
                    };
                    break;
                  case 'quantity-minus':
                  case 'quantity-plus':
                    newElement.attributes = {
                      ...newElement.attributes,
                      'data-product-id': clickedProductData.id
                    };
                    break;
                  case 'remove-button':
                    newElement.attributes = {
                      ...newElement.attributes,
                      'data-product-id': clickedProductData.id
                    };
                    break;
                }

                newElements.push(newElement);

                // Process children recursively and update parent's children array
                if (baseElement.children?.length) {
                  baseElement.children.forEach(childId => {
                    const childElement = currentElements.find(el => el.id === childId);
                    if (childElement) {
                      const newChild = processElement(childElement, newElement.id);
                      newElement.children.push(newChild.id); // Add new child ID to parent's children array
                    }
                  });
                }

                return newElement;
              };

              const newProduct = processElement(testProduct, cartContent.id);
              
              // Update currentElements state
              setCurrentElements(prev => {
                const updated = [...prev, ...newElements];
                
                // Update cart content's children
                const cartContentIndex = updated.findIndex(el => el.id === cartContent.id);
                if (cartContentIndex !== -1) {
                  updated[cartContentIndex] = {
                    ...updated[cartContentIndex],
                    children: [...updated[cartContentIndex].children, newProduct.id]
                  };
                }

                return updated;
              });
            }

            // Show success feedback
            const originalText = button.textContent;
            const originalBg = button.style.backgroundColor;
            button.textContent = 'Added!';
            button.style.backgroundColor = '#4CAF50';
            
            setTimeout(() => {
              button.textContent = originalText;
              button.style.backgroundColor = originalBg;
            }, 2000);

            // Show cart
            const cartWrapper = currentElements.find(el => 
              el.parentId === cart.id && 
              el.type === 'cart-wrapper'
            );
            if (cartWrapper) {
              setCurrentElements(prev => 
                prev.map(el => 
                  el.id === cartWrapper.id 
                    ? { ...el, styles: { ...el.styles, display: 'flex' } }
                    : el
                )
              );
            }
          }}
        >
          {displayText || 'Add to Cart'}
        </button>
      );

    case 'remove-button':
      return (
        <button
          {...elementProps}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            // Find cart item (parent of remove button)
            const cartItem = currentElements.find(el => el.id === element.parentId);
            if (!cartItem) return;

            // Get product ID from cart item
            const productId = cartItem.attributes?.['data-product-id'];
            if (!productId) return;

            // Find cart content (parent of cart item)
            const cartContent = currentElements.find(el => el.id === cartItem.parentId);
            if (!cartContent) return;

            // Find all child elements of this cart item
            const childElements = currentElements.filter(el => el.parentId === cartItem.id);
            const elementIdsToRemove = [cartItem.id, ...childElements.map(el => el.id)];

            // Remove item from localStorage
            try {
              const storedCart = localStorage.getItem(CART_STORAGE_KEY);
              const cartItems = storedCart ? JSON.parse(storedCart) : [];
              const updatedCart = cartItems.filter(item => item.id !== productId);
              saveCartToStorageAndUpdateUI(updatedCart, setCurrentElements);
            } catch (error) {
              console.error('Error updating cart in localStorage:', error);
            }

            // Update elements state
            setCurrentElements(prev => {
              const filteredElements = prev.filter(el => !elementIdsToRemove.includes(el.id));

              const cartContentIndex = filteredElements.findIndex(el => el.id === cartContent.id);
              if (cartContentIndex !== -1) {
                filteredElements[cartContentIndex] = {
                  ...filteredElements[cartContentIndex],
                  children: filteredElements[cartContentIndex].children.filter(id => id !== cartItem.id)
                };
              }

              return filteredElements;
            });
          }}
        >
          {displayText}
        </button>
      );

    case 'quantity-minus':
      return (
        <button
          {...elementProps}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            const productId = element.attributes?.['data-product-id'];
            if (!productId) return;

            // Load current cart items
            const cartItems = loadCartFromStorage();
            const itemIndex = cartItems.findIndex(item => item.id === productId);
            
            if (itemIndex !== -1 && cartItems[itemIndex].quantity > 1) {
              // Update quantity in storage
              cartItems[itemIndex].quantity -= 1;
              saveCartToStorageAndUpdateUI(cartItems, setCurrentElements);

              // Update UI
              setCurrentElements(prev => 
                prev.map(el => {
                  // Update quantity value element
                  if (el.attributes?.['data-element'] === 'quantity-value' && 
                      el.attributes?.['data-product-id'] === productId) {
                    return {
                      ...el,
                      text: cartItems[itemIndex].quantity.toString(),
                      attributes: {
                        ...el.attributes,
                        'data-quantity': cartItems[itemIndex].quantity.toString()
                      }
                    };
                  }
                  // Update cart item element
                  if (el.attributes?.['data-element'] === 'cart-item' && 
                      el.attributes?.['data-product-id'] === productId) {
                    return {
                      ...el,
                      attributes: {
                        ...el.attributes,
                        'data-quantity': cartItems[itemIndex].quantity.toString()
                      }
                    };
                  }
                  return el;
                })
              );
            }
          }}
        >
          {displayText || '-'}
        </button>
      );

    case 'quantity-plus':
      return (
        <button
          {...elementProps}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            const productId = element.attributes?.['data-product-id'];
            if (!productId) return;

            // Load current cart items
            const cartItems = loadCartFromStorage();
            const itemIndex = cartItems.findIndex(item => item.id === productId);
            
            if (itemIndex !== -1) {
              // Update quantity in storage
              cartItems[itemIndex].quantity += 1;
              saveCartToStorageAndUpdateUI(cartItems, setCurrentElements);

              // Update UI
              setCurrentElements(prev => 
                prev.map(el => {
                  // Update quantity value element
                  if (el.attributes?.['data-element'] === 'quantity-value' && 
                      el.attributes?.['data-product-id'] === productId) {
                    return {
                      ...el,
                      text: cartItems[itemIndex].quantity.toString(),
                      attributes: {
                        ...el.attributes,
                        'data-quantity': cartItems[itemIndex].quantity.toString()
                      }
                    };
                  }
                  // Update cart item element
                  if (el.attributes?.['data-element'] === 'cart-item' && 
                      el.attributes?.['data-product-id'] === productId) {
                    return {
                      ...el,
                      attributes: {
                        ...el.attributes,
                        'data-quantity': cartItems[itemIndex].quantity.toString()
                      }
                    };
                  }
                  return el;
                })
              );
            }
          }}
        >
          {displayText || '+'}
        </button>
      );

    case 'cms-content':
      return (
        <div
          {...elementProps}
          onClick={handleInteractionClick}
        >
          <CMSContentElement
            contentTypeId={element.contentTypeId}
            contentType={props.cmsContentTypes?.find(type => type._id === element.contentTypeId)}
            content={props.cmsContent[element.contentTypeId] || []}
            displayType={element.displayType || 'list'}
            limit={element.limit || 10}
            sortBy={element.sortBy || 'createdAt'}
            sortOrder={element.sortOrder || 'desc'}
          >
            {(contentItem) => (
              element.children?.map(childId => {
                const childElement = currentElements.find(el => el.id === childId);
                return childElement ? (
                  <Element 
                    key={`${childId}-${contentItem._id}`}
                    element={childElement}
                    onNavigate={onNavigate}
                    selectedContentItem={contentItem}
                    {...props}
                  />
                ) : null;
              })
            )}
          </CMSContentElement>
        </div>
      );

    case 'image':
      let imageUrl = '';
      
      // Handle dynamic image fields
      if (element.dynamicImageField && props.cmsContent) {
        // Find the content item that matches this element's dynamic field
        const contentItem = selectedContentItem || 
          (props.cmsContent[element.contentTypeId] ? 
            props.cmsContent[element.contentTypeId][0] : null);
            
        if (contentItem && contentItem.data && contentItem.data[element.dynamicImageField]) {
          // Use the dynamic image from content
          imageUrl = contentItem.data[element.dynamicImageField];
        } else {
          // Fallback to a simple gray box instead of external placeholder
          imageUrl = '';
        }
      } else {
        // Use static image source
        imageUrl = element.styles?.backgroundImage ? 
          element.styles.backgroundImage.replace('url(', '').replace(')', '').replace(/"/g, '') : 
          displaySrc || element.src || '';
      }
      
      return (
        <img
          id={element.builderId}
          className={getElementClasses()}
          src={imageUrl}
          alt={displayText || element.text || "Image"}
          style={{
            ...getElementStyles(element, props.classStylesRegistry, props.combinedClassStylesRegistry, selectedDevice),
            ...processAnimationAttributes().styles,
            backgroundColor: !imageUrl ? '#e0e0e0' : undefined
          }}
          onClick={handleInteractionClick}
          {...processedAttributes}
          {...processAnimationAttributes()}
          data-element-type={element.type}
        />
      );

    case 'list':
      return (
        <ul
          {...elementProps}
          onClick={handleInteractionClick}
        >
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </ul>
      );

    case 'listItem':
      return (
        <li
          {...elementProps}
          onClick={handleInteractionClick}
        >
          {displayText}
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </li>
      );

    case 'form':
      const handleFormSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const form = e.target;
        const formData = new FormData(form);
        const data = Object.fromEntries(formData);

        // Check if this is a checkout form
        if (form.getAttribute('name') === 'checkout-form') {          
          // Get cart data from localStorage
          const cartItems = loadCartFromStorage();
          
          // Get the submit button to provide visual feedback
          const submitButton = form.querySelector('[type="submit"]') || form.querySelector('button[type="submit"]');
          const originalBgColor = submitButton ? window.getComputedStyle(submitButton).backgroundColor : '';
          const originalText = submitButton ? submitButton.innerText : '';
          
          // Visual feedback - change button appearance while processing
          if (submitButton) {
            submitButton.style.backgroundColor = '#999999'; // Grey color to indicate processing
            submitButton.innerText = 'Processing...';
            submitButton.disabled = true;
            submitButton.style.cursor = 'wait';
          }
          
          // Function to reset button state with optional success indication
          const resetButton = (success = false) => {
            if (!submitButton) return;
            
            // Re-enable the button
            submitButton.disabled = false;
            submitButton.style.cursor = 'pointer';
            
            if (success) {
              // Show success state briefly
              submitButton.style.backgroundColor = '#4CAF50'; // Green color for success
              submitButton.innerText = 'Completed!';
              
              // Reset to original state after a delay
              setTimeout(() => {
                submitButton.style.backgroundColor = originalBgColor;
                submitButton.innerText = originalText;
              }, 2000);
            } else {
              // Immediately reset to original state on error
              submitButton.style.backgroundColor = originalBgColor;
              submitButton.innerText = originalText;
            }
          };
          
          // Calculate totals
          const subtotal = Number(calculateCheckoutTotal(cartItems)) || 0;
          const shipping = 9.99; // Default shipping cost
          const tax = subtotal * 0.15; // 15% tax rate
          const total = subtotal + shipping + tax;

          // Create customer data object dynamically from form fields
          const customer = {};
          Object.entries(data).forEach(([key, value]) => {
            // Convert field name to camelCase for customer object
            const fieldName = key
              .toLowerCase()
              .replace(/\s+(.)/g, (match, letter) => letter.toUpperCase())
              .replace(/\s+/g, '')
              .replace(/input$/i, ''); // Remove 'Input' suffix if present
            
            customer[fieldName] = value;
          });

          // Compile checkout data
          const checkoutData = {
            customer,
            order: {
              items: cartItems,
              subtotal: subtotal.toFixed(2),
              shipping: shipping.toFixed(2),
              tax: tax.toFixed(2),
              total: total.toFixed(2)
            },
            isPublished: false
          };

          try {
            // Send checkout data to backend
            const response = await fetch(buildApiUrl(`api/sites/${siteId}/orders`), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(checkoutData)
            });

            const result = await response.json();

            if (result.success) {
              // Clear form fields
              form.reset();
              
              // Clear cart
              saveCartToStorage([]);
              updateCartUI([], setCurrentElements);
              
              // Show success feedback
              resetButton(true);
            } else {
              console.error('Order submission failed:', result.error);
              resetButton(false);
            }
          } catch (error) {
            console.error('Error submitting order:', error);
            resetButton(false);
          } finally {
            // Ensure button is always reset even if there's an unexpected error
            setTimeout(() => {
              if (submitButton && submitButton.disabled) {
                resetButton(false);
              }
            }, 5000); // Failsafe: if button hasn't been reset after 5 seconds, force it
          }
          
          return; // Exit early for checkout form
        }

        // Handle other forms
        console.log('Regular Form Data:', data);

        // Find success and error divs using the IDs stored in form element
        const successDiv = document.getElementById(
          currentElements.find(el => el.id === element.successDivId)?.builderId
        );
        const errorDiv = document.getElementById(
          currentElements.find(el => el.id === element.errorDivId)?.builderId
        );

        // If not found, try looking for Contact1 form success and error messages
        let finalSuccessDiv = successDiv;
        let finalErrorDiv = errorDiv;
        
        if (!finalSuccessDiv || !finalErrorDiv) {
          // Check if we're in a Contact1 form by looking at class names or data attributes
          const formWrapper = form.closest('[data-element="contact-form-wrapper"]') || 
                            form.closest('.contact1-form-wrapper');
          
          if (formWrapper) {
            // For Contact1 component, success and error messages are direct children of the wrapper
            finalSuccessDiv = finalSuccessDiv || formWrapper.querySelector('.contact1-form-success') || 
                        formWrapper.querySelector('[data-element="form-success-message"]');
                        
            finalErrorDiv = finalErrorDiv || formWrapper.querySelector('.contact1-form-error') || 
                      formWrapper.querySelector('[data-element="form-error-message"]');
          } else {
            // Try finding messages as siblings of the form
            const formParent = form.parentElement;
            if (formParent) {
              finalSuccessDiv = finalSuccessDiv || formParent.querySelector('.success-message') || 
                          formParent.querySelector('[data-success="true"]');
                          
              finalErrorDiv = finalErrorDiv || formParent.querySelector('.error-message') || 
                        formParent.querySelector('[data-error="true"]');
            }
          }
        }

        // Hide both messages initially
        if (finalSuccessDiv) finalSuccessDiv.style.display = 'none';
        if (finalErrorDiv) finalErrorDiv.style.display = 'none';

        // Get the submit button to provide visual feedback
        const submitButton = form.querySelector('[type="submit"]') || form.querySelector('button[type="submit"]');
        const originalBgColor = submitButton ? window.getComputedStyle(submitButton).backgroundColor : '';
        const originalText = submitButton ? submitButton.innerText : '';
        
        // Visual feedback - change button appearance while processing
        if (submitButton) {
          submitButton.style.backgroundColor = '#999999'; // Grey color to indicate processing
          submitButton.innerText = 'Sending...';
          submitButton.disabled = true;
          submitButton.style.cursor = 'wait';
        }
        
        // Function to reset button state with optional success indication
        const resetButton = (success = false) => {
          if (!submitButton) return;
          
          // Re-enable the button
          submitButton.disabled = false;
          submitButton.style.cursor = 'pointer';
          
          if (success) {
            // Show success state briefly
            submitButton.style.backgroundColor = '#4CAF50'; // Green color for success
            submitButton.innerText = 'Sent!';
            
            // Reset to original state after a delay
            setTimeout(() => {
              submitButton.style.backgroundColor = originalBgColor;
              submitButton.innerText = originalText;
            }, 2000);
          } else {
            // Immediately reset to original state on error
            submitButton.style.backgroundColor = originalBgColor;
            submitButton.innerText = originalText;
          }
        };

        // Check for empty fields
        const hasEmptyFields = Object.values(data).some(value => !value.trim());
        
        if (hasEmptyFields) {
          if (finalErrorDiv) {
            finalErrorDiv.style.display = 'block';
          }
          resetButton(false);
          return;
        }

        // Add siteId to the form data
        const formDataWithSiteId = {
          ...data,
          siteId: siteId
        };

        try {
          // Send form data with siteId to backend
          const response = await fetch(buildApiUrl('api/forms/submit'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(formDataWithSiteId)
          });

          const result = await response.json();

          if (result.success) {
            // Show success message
            if (finalSuccessDiv) {
              finalSuccessDiv.style.display = 'block';
              form.reset(); // Reset form fields
            }
            resetButton(true);
          } else {
            // Show error message
            if (finalErrorDiv) {
              finalErrorDiv.style.display = 'block';
            }
            resetButton(false);
          }
        } catch (error) {
          console.error('Form submission error:', error);
          // Show error message
          if (finalErrorDiv) {
            finalErrorDiv.style.display = 'block';
          }
          resetButton(false);
        }
        
        // Ensure button is always reset even if there's an unexpected error
        setTimeout(() => {
          if (submitButton && submitButton.disabled) {
            resetButton(false);
          }
        }, 5000); // Failsafe: if button hasn't been reset after 5 seconds, force it
      };

      return (
        <form
          {...elementProps}
          onClick={handleInteractionClick}
          name={element.name === undefined ? 'Email Form' : element.name}
          method={element.method || 'get'}
          onSubmit={handleFormSubmit}
        >
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? <Element 
              key={childId}
              element={childElement}
              onNavigate={onNavigate}
              selectedContentItem={selectedContentItem}
              {...props}
            /> : null;
          })}
        </form>
      );

    case 'input': {
      return (
        <input 
          {...elementProps}
          onClick={handleInteractionClick}
          type={element.inputType || 'text'} 
          name={element.name}
          placeholder={element.placeholder || displayText} 
          required={element.required === true}
          autoFocus={element.autofocus === true}
        />
      );
    }

    case 'label':
      return (
        <label 
          {...elementProps}
          onClick={handleInteractionClick}
        >
          {displayText}
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? <Element 
              key={childId}
              element={childElement}
              onNavigate={onNavigate}
              selectedContentItem={selectedContentItem}
              {...props}
            /> : null;
          })}
        </label>
      );

    case 'submit':
      return (
        <button 
          {...elementProps}
          onClick={handleInteractionClick}
          type="submit"
        >
          {displayText || 'Submit'}
        </button>
      );

    case 'textarea':
      return (
        <textarea 
          {...elementProps}
          onClick={handleInteractionClick}
          placeholder={displayText}
          rows={4}
        />
      );
      
    case 'dropdown':
      return (
        <select
          {...elementProps}
          onClick={handleInteractionClick}
        >
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </select>
      );
      
    case 'option':
      return (
        <option 
          {...elementProps}
          value={element.value || ''}
        >
          {displayText}
        </option>
      );

    case 'checkbox':
      return (
        <input 
          {...elementProps}
          type="checkbox"
        />
      );

    case 'radio':
      return (
        <input 
          {...elementProps}
          type="radio"
        />
      );

    case 'video':
      const videoSrc = element.videoSrc || "https://www.youtube.com/embed/dQw4w9WgXcQ";
      const videoType = element.videoType || 'youtube';
      let embedSrc = '';
      
      // Handle different video platforms
      if (videoType === 'youtube') {
        // Extract YouTube video ID if full URL is provided
        const youtubeId = videoSrc.includes('youtube.com') || videoSrc.includes('youtu.be') 
          ? videoSrc.replace(/.*(?:youtu.be\/|v\/|\/v\/|\/embed\/|\/watch\?v=)([^#\&\?]*).*/, '$1')
          : videoSrc;
        embedSrc = `https://www.youtube.com/embed/${youtubeId}`;
      } else if (videoType === 'vimeo') {
        // Extract Vimeo video ID if full URL is provided
        const vimeoId = videoSrc.includes('vimeo.com')
          ? videoSrc.replace(/.*(?:vimeo.com\/)([^#\&\?]*).*/, '$1')
          : videoSrc;
        embedSrc = `https://player.vimeo.com/video/${vimeoId}`;
      } else {
        // Default to treating as direct embed URL
        embedSrc = videoSrc;
      }
      
      // Add query parameters based on settings
      const autoplay = element.autoplay ? '1' : '0';
      const controls = element.controls !== false ? '1' : '0';
      const loop = element.loop ? '1' : '0';
      const mute = element.mute ? '1' : '0';
      
      // Add parameters based on video type
      if (videoType === 'youtube') {
        embedSrc += `?autoplay=${autoplay}&controls=${controls}&loop=${loop}&mute=${mute}`;
      } else if (videoType === 'vimeo') {
        embedSrc += `?autoplay=${autoplay}&loop=${loop}&muted=${mute}`;
      }
      
      return (
        <div 
          {...elementProps}
          onClick={handleInteractionClick}
          data-element-type="video" 
          data-video-type={videoType}
        >
          <iframe 
            src={embedSrc}
            width="100%" 
            height="100%"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Video Player"
          />
        </div>
      );

    case 'embed':
      if (!element.embedCode) {
        // If no embed code is provided, show a placeholder
        return (
          <div
            {...elementProps}
            onClick={handleInteractionClick}
            data-element-type="embed"
          >
            <div style={{textAlign: 'center', padding: '20px'}}>
              Embed code will appear here when published
            </div>
          </div>
        );
      }

      // Sanitize the embed code and render it directly in a container
      // Note: In the actual published site, we'll use dangerouslySetInnerHTML
      return (
        <div
          {...elementProps}
          onClick={handleInteractionClick}
          data-element-type="embed"
        >
          <div style={{textAlign: 'center', padding: '20px'}}>
            Embed code will appear here when published
          </div>
        </div>
      );

    case 'map':
      // Default to a central location if none specified
      const mapLocation = element.mapLocation || 'New York, NY';
      const mapZoom = element.mapZoom || '13';
      const mapQuery = encodeURIComponent(mapLocation);
      const mapSrc = `https://maps.google.com/maps?q=${mapQuery}&t=&z=${mapZoom}&ie=UTF8&iwloc=&output=embed`;
      
      return (
        <div
          {...elementProps}
          onClick={handleInteractionClick}
          data-element-type="map"
        >
          <iframe 
            title="Google Map"
            src={mapSrc}
            width="100%" 
            height="100%" 
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      );

    // Inside the Element component's switch statement, add this case before the default case:

    case 'p':
      return (
        <p
          {...elementProps}
          onClick={handleInteractionClick}
        >
          {displayText}
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </p>
      );

    case 'link':
      return (
        <a
          {...elementProps}
          href="#" 
          onClick={handleInteractionClick}
        >
          {displayText}
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </a>
      );

    case 'link-block':
      return (
        <a
          {...elementProps}
          href="" 
          onClick={handleInteractionClick}
          style={{
            ...elementProps.style,
            display: 'block',
            textDecoration: 'none'
          }}
        >
          {displayText}
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </a>
      );

    case 'nav':
      return (
        <nav
          {...elementProps}
          onClick={handleInteractionClick}
          role="navigation"
          aria-label={element.attributes?.['aria-label'] || 'Main navigation'}
        >
          {displayText}
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </nav>
      );

    case 'table':
      return (
        <table
          {...elementProps}
          onClick={handleInteractionClick}
        >
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </table>
      );

    case 'thead':
      return (
        <thead
          {...elementProps}
          onClick={handleInteractionClick}
        >
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </thead>
      );

    case 'tbody':
      return (
        <tbody
          {...elementProps}
          onClick={handleInteractionClick}
        >
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </tbody>
      );

    case 'tr':
      return (
        <tr
          {...elementProps}
          onClick={handleInteractionClick}
        >
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </tr>
      );

    case 'th':
      return (
        <th
          {...elementProps}
          onClick={handleInteractionClick}
        >
          {displayText}
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </th>
      );

    case 'td':
      return (
        <td
          {...elementProps}
          onClick={handleInteractionClick}
        >
          {displayText}
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </td>
      );

    case 'tab-button':
      return (
        <button {...elementProps} onClick={handleTabInteractionClick}>
          {displayText}
        </button>
      );

    case 'ul':
      return (
        <ul
          {...elementProps}
          onClick={handleInteractionClick}
        >
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </ul>
      );

    default:
      // Select the appropriate HTML tag based on element type
      let Tag = 'div';
      if (element.type === 'button') {
        Tag = 'button';
      } else if (element.type === 'dropdown') {
        Tag = 'select';
      } else if (element.type === 'option') {
        Tag = 'option';
        // Add value attribute for option elements
        elementProps.value = element.value || '';
      }

      return (
        <Tag
          {...elementProps}
          onClick={handleInteractionClick}
          data-slider-role={element.sliderRole}
          data-current-slide={element.sliderRole === 'mask' ? "0" : undefined}
          data-direction={element.direction}
          data-slides-per-view={
            element.sliderRole === 'container' 
              ? (element.deviceSettings?.[selectedDevice]?.slidesPerView || element.slidesPerView || 1) 
              : undefined
          }
          data-slides-per-scroll={
            element.sliderRole === 'container' 
              ? (element.deviceSettings?.[selectedDevice]?.slidesPerScroll || element.slidesPerScroll || 1) 
              : undefined
          }
          data-loop={
            element.sliderRole === 'container'
              ? (element.attributes?.['data-loop'] || 'false')
              : undefined
          }
          data-autoplay={
            element.sliderRole === 'container'
              ? (element.attributes?.['data-autoplay'] || 'false')
              : undefined
          }
          data-delay={
            element.sliderRole === 'container'
              ? (element.attributes?.['data-delay'] || '5000')
              : undefined
          }
          data-element-type={element.sliderRole === 'container' ? 'slider' : undefined}
        >
          {displayText}
          {element.children?.map(childId => {
            const childElement = currentElements.find(el => el.id === childId);
            return childElement ? (
              <Element 
                key={childId}
                element={childElement}
                onNavigate={onNavigate}
                selectedContentItem={selectedContentItem}
                {...props}
              />
            ) : null;
          })}
        </Tag>
      );
  }
});
Element.displayName = 'Element';

const LiveBuild = ({ 
  pages, 
  siteId,
  currentPageId, 
  elements: initialElements, 
  classStylesRegistry,
  combinedClassStylesRegistry,
  onClose,
  cmsContentTypes,
  cmsContent,
  onUpdate,
  setElements
}) => {

  const [selectedDevice, setSelectedDevice] = useState(DEVICES.DESKTOP);
  const [currentPreviewPage, setCurrentPreviewPage] = useState(currentPageId);
  const [liveBuildCurrentPageId, setLiveBuildCurrentPageId] = useState(currentPageId);
  const [currentElements, setCurrentElements] = useState(initialElements || []);
  const [currentStyles, setCurrentStyles] = useState(classStylesRegistry);
  const [selectedContentItem, setSelectedContentItem] = useState(null);
  const [currentContentItemId, setCurrentContentItemId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  
  useEffect(() => {
    console.log('currentElements Test', currentElements);
  }, [currentElements]);

  // Update updateCartTotalUI to use the top-level functions
  const updateCartTotalUI = useCallback(() => {
    const cartItems = loadCartFromStorage();
    updateCartUI(cartItems, setCurrentElements);
  }, []);

  // Update handleClose
  const handleClose = useCallback(() => {
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
      updateCartUI([], setCurrentElements);
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  }, [onClose]);
  
  
  // Add this new useEffect to sync elements
  useEffect(() => {
    if (isInitialized) {
      setCurrentElements(initialElements);
    }
  }, [initialElements, isInitialized]);

  // Initialize elements with tab states
  useEffect(() => {
    // Only run once on mount
    console.log('isInitialized', isInitialized);
    if (isInitialized) return;

    const initializeElements = () => {
      const tabGroups = {};
      initialElements.forEach(el => {
        if (el.type === 'button' && el.relatedContentId && el.tabGroupId) {
          if (!tabGroups[el.tabGroupId]) {
            tabGroups[el.tabGroupId] = [];
          }
          tabGroups[el.tabGroupId].push(el);
        }
      });

      if (Object.keys(tabGroups).length > 0) {
        const updatedElements = initialElements.map(el => {
          if (el.tabGroupId) {
            const groupTabs = tabGroups[el.tabGroupId];
            const defaultTab = groupTabs.find(tab => tab.byDefault === true) || groupTabs[0];

            if (defaultTab) {
              if (el.type === 'button') {
                return {
                  ...el,
                  className: el.id === defaultTab.id ? 'Tab active' : 'Tab'
                };
              } else {
                return {
                  ...el,
                  styles: {
                    ...el.styles,
                    display: el.id === defaultTab.relatedContentId ? 'block' : 'none'
                  }
                };
              }
            }
          }
          return el;
        });

        setCurrentElements(updatedElements);
        onUpdate(updatedElements);
      } else {
        setCurrentElements(initialElements);
      }
    };

    initializeElements();
    setIsInitialized(true);
  }, []);

  
  // Update the useEffect that sets selectedContentItem
  useEffect(() => {
    // Add null check for currentElements
    if (!currentElements) return;

    // Check if we're on a template page
    const isTemplatePage = currentPreviewPage.startsWith('template-');
    
    if (isTemplatePage && currentContentItemId) {
      // For template pages, find the content type ID from the page ID
      const contentTypeId = currentPreviewPage.replace('template-', '');
      const contentItems = cmsContent[contentTypeId] || [];
      
      // Find the specific content item
      const specificItem = contentItems.find(item => item._id === currentContentItemId);
      if (specificItem) {
        setSelectedContentItem(specificItem);
        return;
      }
    } else {
      // For regular pages with CMS elements
      const cmsElements = currentElements.filter(el => el.type === 'cms-content');
      
      if (cmsElements.length > 0 && cmsElements[0].contentTypeId) {
        const contentItems = cmsContent[cmsElements[0].contentTypeId] || [];
        
        // If we have a specific content item ID, use that
        if (currentContentItemId && contentItems.length > 0) {
          const specificItem = contentItems.find(item => item._id === currentContentItemId);
          if (specificItem) {
            setSelectedContentItem(specificItem);
            return;
          }
        }
        
        // Fallback to first item if no specific item found
        if (contentItems.length > 0) {
          setSelectedContentItem(contentItems[0]);
        }
      }
    }
    
    // Initialize cart buttons after content is set
    // initializeCartButtons();
  }, [currentElements, cmsContent, currentContentItemId, currentPreviewPage]);

  // Update handleDeviceSelect to use correct preview widths
  const handleDeviceSelect = (device) => {
    setSelectedDevice(device);
    
    // Get preview width based on device
    const previewWidth = device === DEVICES.MOBILE ? 375 : 
                        device === DEVICES.TABLET ? 768 : 
                        1440;
    
    // Set data-device attribute on container
    const container = document.querySelector('.live-preview-container');
    if (container) {
      container.setAttribute('data-device', device.toLowerCase());
    }
    
    // Log device change and check media queries
    console.log('Device Changed:', device);
    checkMediaQueries(previewWidth);
  };

  // Update stylesheet management useEffect
  useEffect(() => {
    // Create both stylesheets
    if (styleSheetRef && document.head.contains(styleSheetRef)) {
      document.head.removeChild(styleSheetRef);
      styleSheetRef = null;
    }
    createStyleSheet(currentStyles, combinedClassStylesRegistry);
    createCustomCssStyleSheet(initialElements);

    // Cleanup function
    return () => {
      if (styleSheetRef && document.head.contains(styleSheetRef)) {
        document.head.removeChild(styleSheetRef);
        styleSheetRef = null;
      }
      if (customCssStyleSheetRef && document.head.contains(customCssStyleSheetRef)) {
        document.head.removeChild(customCssStyleSheetRef);
        customCssStyleSheetRef = null;
      }
    };
  }, [currentStyles, combinedClassStylesRegistry, initialElements, currentPreviewPage]);

  // Add effect to update custom CSS when elements change
  useEffect(() => {
    if (currentElements.length > 0) {
      createCustomCssStyleSheet(currentElements);
    }
  }, [currentElements]);

  // Update elements and styles when page changes
  const updatePageContent = useCallback((pageId) => {
    const newPage = pages.find(p => p.id === pageId);
    if (newPage) {
      setCurrentPreviewPage(pageId);
      setCurrentElements(newPage.elements || []);
      setCurrentStyles(newPage.classStylesRegistry || classStylesRegistry);
    }
  }, [pages, classStylesRegistry]);

  // Initialize with current page content
  useEffect(() => {
    updatePageContent(currentPageId);
  }, [currentPageId, updatePageContent]);

  // Update page navigation handler to accept contentItemId
  const handlePageNavigation = useCallback((pageId, contentItemId, updatedElements) => {
    if (updatedElements) {
      // Handle tab switching
      setCurrentElements(updatedElements);
      onUpdate(updatedElements);
    } else if (pageId) {
      console.log('handlePageNavigation', { pageId, contentItemId });
      
      if (contentItemId) {
        setCurrentContentItemId(contentItemId);
      }

      console.log("pages:", pages);
      
      // Handle special case: if slug is 'index', convert to 'home'
      let searchPageId = pageId;
      if (pageId === 'index') {
        searchPageId = 'home';
      }
      
      // Find page by slug
      const foundPage = pages.find(p => p.slug === searchPageId);
      console.log("found page by slug:", foundPage);
      
      // If we found a page by slug, use its ID, otherwise use the searchPageId as-is
      const actualPageId = foundPage ? foundPage.id : searchPageId;
      console.log("actualPageId:", actualPageId);
      
      // Update both page IDs
      setCurrentPreviewPage(actualPageId);
      setLiveBuildCurrentPageId(actualPageId);
      
      // Update page content
      updatePageContent(actualPageId);
    }
  }, [updatePageContent, onUpdate, pages]);

  // Function to populate cart with stored items
  const populateCartWithStoredItems = useCallback((storedItems) => {
    if (!storedItems.length) return;

    setCurrentElements(prev => {
      // Find necessary elements in the current state
      const cart = prev.find(el => 
        el.type === 'cart' && 
        el.cartPageId === liveBuildCurrentPageId
      );
      if (!cart) return prev;

      const cartContent = prev.find(el => 
        el.type === 'cart-content'
      );
      if (!cartContent) return prev;

      const testProduct = prev.find(el => 
        el.parentId === cartContent.id && 
        el.attributes && 
        el.attributes['data-element'] === 'cart-item' &&
        el.attributes['data-product-id'] === 'Test-Product-1'
      );
      if (!testProduct) return prev;

      // Create new elements for each stored item
      const newElements = [];
      const updatedCartContentChildren = [...cartContent.children];

      storedItems.forEach(item => {
        const processElement = (baseElement, parentId) => {
          const newElement = {
            ...baseElement,
            id: Date.now() + Math.floor(Math.random() * 1000),
            builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            parentId,
            children: [], // Initialize empty children array
            styles: { ...baseElement.styles, display: 'flex' }
          };

          // Update attributes based on element's data-element attribute
          const elementRole = baseElement.attributes?.['data-element'];
          
          switch (elementRole) {
            case 'cart-item':
              newElement.attributes = {
                ...newElement.attributes,
                'data-product-id': item.id,
                'data-price': item.price,
                'data-quantity': item.quantity.toString()
              };
              break;
            case 'product-name':
              newElement.text = item.name;
              break;
            case 'product-price':
              newElement.text = `$${item.price}`;
              newElement.attributes = {
                ...newElement.attributes,
                'data-price': item.price
              };
              break;
            case 'product-image':
              newElement.src = item.image;
              break;
            case 'quantity-value':
              newElement.text = item.quantity.toString();
              newElement.attributes = {
                ...newElement.attributes,
                'data-element': 'quantity-value',
                'data-quantity': item.quantity.toString(),
                'data-product-id': item.id  // Add this line
              };
              break;
            case 'quantity-minus':
            case 'quantity-plus':
              newElement.attributes = {
                ...newElement.attributes,
                'data-product-id': item.id
              };
              break;
            case 'remove-button':
              newElement.attributes = {
                ...newElement.attributes,
                'data-product-id': item.id
              };
              break;
          }

          newElements.push(newElement);

          // Process children recursively and update parent's children array
          if (baseElement.children?.length) {
            baseElement.children.forEach(childId => {
              const childElement = prev.find(el => el.id === childId);
              if (childElement) {
                const newChild = processElement(childElement, newElement.id);
                newElement.children.push(newChild.id); // Add new child ID to parent's children array
              }
            });
          }

          return newElement;
        };

        const newProduct = processElement(testProduct, cartContent.id);
        updatedCartContentChildren.push(newProduct.id);
      });

      // Create updated state with new elements
      const updated = [...prev];
      
      // Update cart content's children
      const cartContentIndex = updated.findIndex(el => el.id === cartContent.id);
      if (cartContentIndex !== -1) {
        updated[cartContentIndex] = {
          ...updated[cartContentIndex],
          children: updatedCartContentChildren
        };
      }

      // Add all new elements
      return [...updated, ...newElements];
    });

    // Update cart total after populating items
    updateCartTotalUI();
  }, [liveBuildCurrentPageId, updateCartTotalUI]);

  // Load cart items on mount and page change
  useEffect(() => {
    const storedItems = loadCartFromStorage();
    if (storedItems.length > 0) {
      populateCartWithStoredItems(storedItems);
    }
  }, [loadCartFromStorage, populateCartWithStoredItems, liveBuildCurrentPageId]);

  // Update ElementWithCMS component
  const ElementWithCMS = useCallback(({ element, ...props }) => {
    if (element.type === 'cms-content' && element.contentTypeId) {
      return (
        <Element 
          {...props} 
          element={element}
          currentPageId={liveBuildCurrentPageId}
          cmsContentTypes={cmsContentTypes}
          cmsContent={cmsContent}
          classStylesRegistry={props.classStylesRegistry}
          combinedClassStylesRegistry={props.combinedClassStylesRegistry}
          selectedDevice={props.selectedDevice}
          onUpdate={props.onUpdate}
          currentElements={currentElements}
          setCurrentElements={props.setCurrentElements}
        />
      );
    }
    return (
      <Element 
        {...props} 
        element={element}
        currentPageId={liveBuildCurrentPageId}
        cmsContentTypes={cmsContentTypes}
        cmsContent={cmsContent}
        classStylesRegistry={props.classStylesRegistry}
        combinedClassStylesRegistry={props.combinedClassStylesRegistry}
        selectedDevice={props.selectedDevice}
        onUpdate={props.onUpdate}
        currentElements={currentElements}
        setCurrentElements={props.setCurrentElements}
      />
    );
  }, [cmsContentTypes, cmsContent, liveBuildCurrentPageId, currentElements, setCurrentElements]);

  // Update the Intersection Observer setup
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          const animationProps = element.getAttribute('data-animation-onview');
          if (animationProps) {
            // Reset any existing animation
            element.style.animation = 'none';
            // Force reflow
            const reflow = element.offsetHeight;
            // Apply all animations
            element.style.animation = animationProps;
            element.style.animationFillMode = 'both';
            // Optional: unobserve after animation is applied
            observer.unobserve(element);
          }
        }
      });
    }, { 
      threshold: 0.1,
      rootMargin: '50px'
    });

    // Find and observe elements with onView animations
    const elementsToObserve = document.querySelectorAll('[data-animation-onview]');
    elementsToObserve.forEach(element => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, [currentElements]);

  // Add this function in the LiveBuild component
  const populateCheckoutOrderItems = useCallback(() => {
    // First check if we're on a checkout page
    const isCheckoutPage = currentElements.some(el => 
      el.attributes?.['data-element'] === 'checkout-section'
    );

    if (!isCheckoutPage) return;

    // Load cart items from storage
    const cartItems = loadCartFromStorage();
    if (!cartItems.length) return;

    // Find the template order item
    const templateOrderItem = currentElements.find(el => 
      el.attributes?.['data-element'] === 'checkout-order-item' && 
      el.attributes?.['data-template'] === 'true'
    );

    if (!templateOrderItem) return;

    // Find the order items container
    const orderItemsContainer = currentElements.find(el => 
      el.attributes?.['data-element'] === 'checkout-order-items'
    );

    if (!orderItemsContainer) return;

    setCurrentElements(prev => {
      // First, remove all existing order items (except the template)
      const existingOrderItems = prev.filter(el => 
        el.attributes?.['data-element'] === 'checkout-order-item' && 
        el.attributes?.['data-template'] !== 'true'
      );
      
      // Get all IDs to remove (including children of order items)
      const idsToRemove = new Set();
      existingOrderItems.forEach(item => {
        idsToRemove.add(item.id);
        const addDescendantIds = (elementId) => {
          const element = prev.find(el => el.id === elementId);
          if (element && element.children) {
            element.children.forEach(childId => {
              idsToRemove.add(childId);
              addDescendantIds(childId);
            });
          }
        };
        addDescendantIds(item.id);
      });

      // Filter out all removed elements
      const filteredElements = prev.filter(el => !idsToRemove.has(el.id));

      // Get the base children (non-order items)
      const baseChildren = orderItemsContainer.children.filter(childId => {
        const child = prev.find(el => el.id === childId);
        return !child || child.attributes?.['data-element'] !== 'checkout-order-item' || 
               child.attributes?.['data-template'] === 'true';
      });

      const newElements = [];
      const updatedOrderItemsChildren = [...baseChildren];

      // Process each cart item
      cartItems.forEach(cartItem => {
        const processElement = (baseElement, parentId) => {
          const newElement = {
            ...baseElement,
            id: Date.now() + Math.floor(Math.random() * 1000),
            builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            parentId,
            children: [],
            attributes: {
              ...baseElement.attributes,
              'data-template': 'false',
              'data-product-id': cartItem.id
            }
          };

          // Update text/data based on field type
          if (baseElement.attributes?.['data-field']) {
            switch (baseElement.attributes['data-field']) {
              case 'checkout-product-name':
                newElement.text = cartItem.name;
                break;
              case 'checkout-product-quantity':
                newElement.text = `x${cartItem.quantity}`;
                break;
              case 'checkout-product-price':
                const totalPrice = (parseFloat(cartItem.price) * cartItem.quantity).toFixed(2);
                newElement.text = `$${totalPrice}`;
                break;
            }
          }

          newElements.push(newElement);

          // Process children recursively
          if (baseElement.children?.length) {
            baseElement.children.forEach(childId => {
              const childElement = prev.find(el => el.id === childId);
              if (childElement) {
                const newChild = processElement(childElement, newElement.id);
                newElement.children.push(newChild.id);
              }
            });
          }

          return newElement;
        };

        // Create new order item and add to order items container children
        const newOrderItem = processElement(templateOrderItem, orderItemsContainer.id);
        updatedOrderItemsChildren.push(newOrderItem.id);
      });

      // Calculate totals
      let subtotal = 0;
      cartItems.forEach(item => {
        subtotal += parseFloat(item.price) * item.quantity;
      });

      const shipping = 9.99;
      const tax = subtotal * 0.1;
      const total = subtotal + shipping + tax;

      // Update elements with new structure
      return [...filteredElements, ...newElements].map(el => {
        if (el.id === orderItemsContainer.id) {
          return {
            ...el,
            children: updatedOrderItemsChildren
          };
        }
        // Hide template items
        if (el.attributes?.['data-template'] === 'true') {
          return {
            ...el,
            className: `${el.className || ''} ${templateHiddenClass}`.trim(),
            styles: {
              ...el.styles
            }
          };
        }
        // Update totals
        if (el.attributes?.['data-field'] === 'shipping-price') {
          return { ...el, text: `$${shipping.toFixed(2)}` };
        }
        if (el.attributes?.['data-field'] === 'tax-amount') {
          return { ...el, text: `$${tax.toFixed(2)}` };
        }
        if (el.attributes?.['data-field'] === 'total-amount') {
          return { ...el, text: `$${total.toFixed(2)}` };
        }
        return el;
      });
    });
  }, [currentElements]);

  // Add a ref to track if we've already populated the checkout items
  const hasPopulatedCheckout = useRef(false);

  // Modify the useEffect to prevent infinite loop
  useEffect(() => {
    const isCheckoutPage = currentElements.some(el => 
      el.attributes?.['data-element'] === 'checkout-section'
    );

    if (isCheckoutPage && !hasPopulatedCheckout.current) {
      console.log("Populating checkout page items");
      hasPopulatedCheckout.current = true;
      populateCheckoutOrderItems();
    } else if (!isCheckoutPage) {
      // Reset the flag when we leave the checkout page
      hasPopulatedCheckout.current = false;
    }
  }, [currentPreviewPage]); // Only run when page changes

  // Initialize sliders when component mounts or elements/device changes
  useEffect(() => {
    // Function to initialize sliders
    const initializeSliders = () => {
      const sliders = document.querySelectorAll('[data-slider-role="container"]');
      
      sliders.forEach(slider => {
        const mask = slider.querySelector('[data-slider-role="mask"]');
        if (!mask) return;
        
        // Get configuration from slider attributes
        const slidesPerView = parseInt(slider.getAttribute('data-slides-per-view') || '1');
        const loopEnabled = slider.getAttribute('data-loop') === 'true';
        const autoplayEnabled = slider.getAttribute('data-autoplay') === 'true';
        const autoplayDelay = parseInt(slider.getAttribute('data-delay') || '5000');
        
        // Apply styles to slides
        const slides = mask.querySelectorAll('[data-slider-role="slide"]');
        slides.forEach(slide => {
          slide.style.flex = `0 0 ${100/slidesPerView}%`;
        });
        
        // Reset transform
        mask.style.transform = 'translateX(0)';
        mask.setAttribute('data-current-slide', '0');
        
        // Variables to track dragging state
        let isDragging = false;
        let startPosition = 0;
        let currentTranslate = 0;
        let prevTranslate = 0;
        let animationID = 0;
        
        // Clean up old event listeners first to prevent duplicates
        mask.removeEventListener('mousedown', dragStart);
        mask.removeEventListener('touchstart', dragStart);
        document.removeEventListener('mousemove', drag);
        document.removeEventListener('touchmove', drag);
        document.removeEventListener('mouseup', dragEnd);
        document.removeEventListener('touchend', dragEnd);
        document.removeEventListener('mouseleave', dragEnd);
        
        // Set up autoplay functionality
        let autoplayInterval = null;
        
        // Helper function to calculate slides
        const calculateSlidePosition = (current) => {
          // Ensure we're using the exact percentage for proper alignment
          return -current * (100/slidesPerView);
        };
        
        // Function to handle drag start
        function dragStart(e) {
          // Pause autoplay during dragging
          if (autoplayInterval) {
            clearInterval(autoplayInterval);
          }
          
          if (e.type === 'touchstart') {
            startPosition = e.touches[0].clientX;
          } else {
            startPosition = e.clientX;
            e.preventDefault(); // Prevent default behavior for mouse events
          }
          
          isDragging = true;
          
          // Get current transform value
          const style = window.getComputedStyle(mask);
          
          try {
            // Try to use WebKitCSSMatrix (supported in most modern browsers)
            const matrix = new (window.WebKitCSSMatrix || window.DOMMatrix || window.MSCSSMatrix)(style.transform);
            prevTranslate = matrix.m41; // Get the X translation
          } catch (err) {
            // Fallback for browsers without WebKitCSSMatrix support
            const transformValue = style.transform || style.webkitTransform;
            const match = transformValue.match(/matrix.*\((.+)\)/);
            if (match) {
              const values = match[1].split(', ');
              prevTranslate = parseFloat(values[4] || 0);
            }
          }
          
          // Use requestAnimationFrame to make animation smooth
          cancelAnimationFrame(animationID);
          animationID = requestAnimationFrame(animation);
        }
        
        function animation() {
          if (isDragging) {
            requestAnimationFrame(animation);
          }
        }
        
        // Function to handle dragging
        function drag(e) {
          if (!isDragging) return;
          
          let currentPosition;
          if (e.type === 'touchmove') {
            currentPosition = e.touches[0].clientX;
          } else {
            currentPosition = e.clientX;
          }
          
          // Calculate drag distance
          const diff = currentPosition - startPosition;
          
          // Calculate what the new position would be
          currentTranslate = prevTranslate + diff;
          
          // Apply the transform without transition for smooth dragging
          mask.style.transition = 'none';
          
          // Convert pixel translation to a percentage for consistency with autoplay
          const maskWidth = mask.offsetWidth;
          const translatePercent = (currentTranslate / maskWidth) * 100;
          
          // Apply translation as percentage for consistency with other transforms
          mask.style.transform = `translateX(${translatePercent}%)`;
        }
        
        // Function to handle drag end and snap to slide
        function dragEnd() {
          if (!isDragging) return;
          
          isDragging = false;
          cancelAnimationFrame(animationID);
          
          // Re-enable transition for smooth snap
          mask.style.transition = 'transform 0.3s ease-out';
          
          // Calculate the slide width in pixels
          const slideWidth = mask.offsetWidth / slidesPerView;
          const totalSlides = slides.length;
          
          // Calculate which slide to snap to (based on distance dragged)
          const movePercentage = currentTranslate - prevTranslate;
          const slidesPerScroll = parseInt(slider.getAttribute('data-slides-per-scroll') || '1');
          
          let currentSlide = parseInt(mask.getAttribute('data-current-slide') || '0');
          
          // If dragged more than 20% of slide width, move to next or previous slide
          if (Math.abs(movePercentage) > slideWidth * 0.2) {
            if (movePercentage > 0) {
              // Dragged right (to previous slides)
              if (currentSlide === 0 && loopEnabled) {
                // If at the beginning and loop is enabled, go to the end
                currentSlide = totalSlides - slidesPerView;
              } else {
                // Otherwise go to previous slide
                currentSlide = Math.max(0, currentSlide - slidesPerScroll);
              }
            } else {
              // Dragged left (to next slides)
              if (currentSlide + slidesPerScroll + slidesPerView > totalSlides && loopEnabled) {
                // If would exceed end and loop is enabled, go to the beginning
                currentSlide = 0;
              } else {
                // Otherwise go to next slide
                currentSlide = Math.min(totalSlides - slidesPerView, currentSlide + slidesPerScroll);
              }
            }
          }
          
          // Set to the calculated slide position
          const position = calculateSlidePosition(currentSlide);
          mask.style.transform = `translateX(${position}%)`;
          mask.setAttribute('data-current-slide', currentSlide);
          
          // Restart autoplay if it was enabled
          startAutoplay();
        }
        
        // Function to start autoplay
        function startAutoplay() {
          if (autoplayEnabled) {
            if (autoplayInterval) {
              clearInterval(autoplayInterval);
            }
            
            autoplayInterval = setInterval(() => {
              // Always get fresh values in case they changed
              const slidesPerScroll = parseInt(slider.getAttribute('data-slides-per-scroll') || '1');
              let currentSlide = parseInt(mask.getAttribute('data-current-slide') || '0');
              
              // Move by the same number of slides as arrow buttons (slidesPerScroll)
              let nextSlide = currentSlide + slidesPerScroll;
              
              // Handle reaching the end
              if (nextSlide + slidesPerView > slides.length) {
                if (loopEnabled) {
                  // If loop is enabled, go back to first slide
                  nextSlide = 0;
                } else {
                  // If at the end and loop is disabled, stop at the last possible position
                  if (currentSlide >= slides.length - slidesPerView) {
                    clearInterval(autoplayInterval);
                    return;
                  } else {
                    // Go to the last possible position
                    nextSlide = slides.length - slidesPerView;
                  }
                }
              }
              
              // Apply transition smoothly
              mask.style.transition = 'transform 0.5s ease-out';
              const position = calculateSlidePosition(nextSlide);
              mask.style.transform = `translateX(${position}%)`;
              mask.setAttribute('data-current-slide', nextSlide);
            }, autoplayDelay);
          }
        }
        
        // Add event listeners for drag functionality
        mask.addEventListener('mousedown', dragStart);
        mask.addEventListener('touchstart', dragStart, { passive: true });
        document.addEventListener('mousemove', drag);
        document.addEventListener('touchmove', drag, { passive: true });
        document.addEventListener('mouseup', dragEnd);
        document.addEventListener('touchend', dragEnd);
        document.addEventListener('mouseleave', dragEnd);
        
        // Start autoplay if enabled
        startAutoplay();
        
        // Store references to functions and interval for cleanup
        slider._autoplayInterval = autoplayInterval;
        mask._dragStart = dragStart;
        mask._drag = drag;
        mask._dragEnd = dragEnd;
      });
    };
    
    // Wait for DOM to be ready
    setTimeout(initializeSliders, 100);
    
    // Also run on window resize
    const handleResize = () => {
      setTimeout(initializeSliders, 100);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      // Clean up all event listeners and timers
      window.removeEventListener('resize', handleResize);
      
      // Clean up all autoplay intervals and event listeners
      const sliders = document.querySelectorAll('[data-slider-role="container"]');
      sliders.forEach(slider => {
        if (slider._autoplayInterval) {
          clearInterval(slider._autoplayInterval);
        }
        
        const mask = slider.querySelector('[data-slider-role="mask"]');
        if (mask) {
          // Remove event listeners
          if (mask._dragStart) {
            mask.removeEventListener('mousedown', mask._dragStart);
            mask.removeEventListener('touchstart', mask._dragStart);
          }
          if (mask._drag) {
            document.removeEventListener('mousemove', mask._drag);
            document.removeEventListener('touchmove', mask._drag);
          }
          if (mask._dragEnd) {
            document.removeEventListener('mouseup', mask._dragEnd);
            document.removeEventListener('touchend', mask._dragEnd);
            document.removeEventListener('mouseleave', mask._dragEnd);
          }
        }
      });
    };
  }, [currentElements, selectedDevice]);

  return (
    <SiteContext.Provider value={{ siteId }}>
      <CurrentElementsContext.Provider value={{ currentElements, setCurrentElements }}>
        <div className="live-build-container">
          <div className="live-build-header">
            <div className="live-build-title">
              <h2>Live Preview</h2>
              <span className="current-page">
                {pages?.find(p => p.id === currentPreviewPage)?.name || 'Preview'}
              </span>
            </div>
            <div className="device-buttons">
              <button 
                className={`device-btn ${selectedDevice === DEVICES.MOBILE ? 'active' : ''}`}
                onClick={() => handleDeviceSelect(DEVICES.MOBILE)}
              >
                <span role="img" aria-label="Mobile">📱</span> Mobile
              </button>
              <button 
                className={`device-btn ${selectedDevice === DEVICES.TABLET ? 'active' : ''}`}
                onClick={() => handleDeviceSelect(DEVICES.TABLET)}
              >
                <span role="img" aria-label="Tablet">📱</span> Tablet
              </button>
              <button 
                className={`device-btn ${selectedDevice === DEVICES.DESKTOP ? 'active' : ''}`}
                onClick={() => handleDeviceSelect(DEVICES.DESKTOP)}
              >
                <span role="img" aria-label="Desktop">💻</span> Desktop
              </button>
            </div>
            <button onClick={handleClose} className="close-button">×</button>
          </div>
          <div className="live-build-content">
            <div className="preview-container-live-build">
              <div 
                className="live-preview-container"
                data-device={selectedDevice.toLowerCase()}
                style={{ 
                  width: selectedDevice === DEVICES.MOBILE ? '375px' : 
                         selectedDevice === DEVICES.TABLET ? '768px' : 
                         '100%',
                  maxWidth: '1440px',
                  margin: '0 auto',
                  transition: 'width 0.3s ease',
                  boxSizing: 'border-box',
                  position: 'relative'
                }}
              >
                {currentElements
                  .filter(element => !element.parentId)
                  .map(element => (
                    <ElementWithCMS
                      key={element.id}
                      element={element}
                      elements={currentElements}
                      onNavigate={handlePageNavigation}
                      selectedContentItem={selectedContentItem}
                      cmsContentTypes={cmsContentTypes}
                      cmsContent={cmsContent}
                      classStylesRegistry={currentStyles}
                      combinedClassStylesRegistry={combinedClassStylesRegistry}
                      selectedDevice={selectedDevice}
                      onUpdate={onUpdate}
                      currentPageId={liveBuildCurrentPageId}
                      currentElements={currentElements}
                      setCurrentElements={setCurrentElements}
                    />
                  ))
                }
              </div>
            </div>
          </div>
        </div>
        </CurrentElementsContext.Provider>
      </SiteContext.Provider>
    );
};

// Memoize the entire LiveBuild component
export default memo(LiveBuild);